import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Pause, Square, MapPin, Wrench, CheckCircle, X, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useDispatch, useSelector } from 'react-redux';
import { getSpareParts } from '../../redux/features/sparePartSlice';
import { fetchDeviceCategories, fetchServicesList, analyzeDeviceIssue } from '../../redux/api';
import { updateClientAsync, createClientAsync } from '../../redux/features/clientSlice';
import { createJob, getJobs, updateJob, updateJobInArray } from '../../redux/features/jobSlice';
import { API_BASE_URL } from '../../config/api.js';
import { formatDisplayValue, parseMoneyAmountSR } from '../../utils/displayUtils';
import AIDiagnosisModal from './AIDiagnosisModal';
import SignaturePad from './SignaturePad';
import {
  isSubcontractPartnerJob,
  canShowPartnerArchiveControls
} from '../../utils/handoffWorkflowUtils';
import JobWithdrawnMaterialsPicker from '../inventory/JobWithdrawnMaterialsPicker';
import { confirmOpenWithdrawnItemsBeforeJobClose } from '../../utils/checkOpenWithdrawnItemsForJob';
import { markMovementAsUsed } from '../../redux/api';
import WorkOrderPrintModal from '../jobs/WorkOrderPrintModal';

const JobStatusControls = ({ job, user, onStatusUpdate, globalUseAI = false, globalEnableParking = false }) => {
  const { t } = useTranslation();
  const { emitJobStatusUpdate, emitWorkerStatusUpdate } = useWebSocket();

  const mapJobStatusToWorkerStatus = (jobStatus) => {
    switch ((jobStatus || '').toLowerCase()) {
      case 'on road':
        return 'on_the_road';
      case 'at client':
        return 'at_client';
      case 'in repair':
      case 'in progress':
        return 'at_client';
      case 'on break':
        return 'on_break';
      default:
        return (jobStatus || 'available').toLowerCase().replace(/\s+/g, '_');
    }
  };
  const dispatch = useDispatch();
  const { items: spareParts, loading: sparePartsLoading } = useSelector((state) => state.spareParts || { items: [], loading: false });
  const jobState = useSelector((state) => state.job || { jobs: [] });
  const allJobs = useMemo(() => {
    return Array.isArray(jobState.jobs) ? jobState.jobs : [];
  }, [jobState.jobs]);
  
  // Check if user is owner (company) or worker
  const userType = typeof window !== 'undefined' ? localStorage.getItem('userType') : 'company';
  const isWorker = userType === 'worker';
  const isSubcontractHandoff = isSubcontractPartnerJob(job);
  const showPartnerArchiveControls = canShowPartnerArchiveControls(job);

  const handleArchiveJob = async () => {
    try {
      setLoading(true);
      const token = user?.token;
      const isSubcontractFromCentral = !!job?.sourceCompanyId;

      if (isSubcontractFromCentral) {
        await axios.patch(
          `${API_BASE_URL}/api/jobs/${job._id}`,
          { handoffPartnerClosed: true, handoffWorkflowPhase: 'closed' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Posao zatvoren — arhivira centralni servis nakon obračuna');
        dispatch(updateJobInArray({
          jobId: job._id,
          updates: { handoffPartnerClosed: true, handoffWorkflowPhase: 'closed' }
        }));
        return;
      }

      const canArchive = await confirmOpenWithdrawnItemsBeforeJobClose(
        job._id,
        job.status,
        'Archived'
      );
      if (!canArchive) return;

      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        { status: 'Archived' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Posao je arhiviran');
      if (onStatusUpdate) onStatusUpdate('Archived');
      emitJobStatusUpdate({
        jobId: job._id,
        status: 'Archived',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error archiving job:', error);
      toast.error('Greška pri arhiviranju posla');
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerHandoffClose = async () => {
    try {
      setLoading(true);
      const token = user?.token;
      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        { handoffPartnerClosed: true, handoffWorkflowPhase: 'closed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stranka spremna za štampu i arhivu');
      dispatch(updateJobInArray({
        jobId: job._id,
        updates: { handoffPartnerClosed: true, handoffWorkflowPhase: 'closed' }
      }));
    } catch (error) {
      console.error('Error closing partner handoff:', error);
      toast.error('Greška pri završavanju stranke');
    } finally {
      setLoading(false);
    }
  };

  const renderFinishedJobActions = () => {
    if (job?.status !== 'Finished') return null;

    if (isSubcontractHandoff && !showPartnerArchiveControls) {
      return (
        <div className="mt-3">
          <button
            onClick={handlePartnerHandoffClose}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
          >
            <CheckCircle className="h-4 w-4" />
            {loading ? 'Završava se...' : 'Završi stranku'}
          </button>
        </div>
      );
    }

    return (
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => {
            setShowPrintModal(true);
          }}
          className="w-full sm:w-1/2 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm"
        >
          <Printer className="h-4 w-4" />
          Radni nalog / Štampa
        </button>
        <button
          onClick={handleArchiveJob}
          disabled={loading}
          className="w-full sm:w-1/2 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
        >
          <CheckCircle className="h-4 w-4" />
          {loading
            ? 'Završava se...'
            : job?.sourceCompanyId
              ? 'Zatvori stranku'
              : 'Arhiviraj'}
        </button>
      </div>
    );
  };
  
  const [currentStatus, setCurrentStatus] = useState('pending');
  const [isTraveling, setIsTraveling] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [travelTime, setTravelTime] = useState(0);
  const [workTime, setWorkTime] = useState(0);
  const [timeLogs, setTimeLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [travelStartTime, setTravelStartTime] = useState(null);
  const [workStartTime, setWorkStartTime] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showParkingModal, setShowParkingModal] = useState(false);
  const [showCustomerSignatureFullScreen, setShowCustomerSignatureFullScreen] = useState(false);
  // Use global settings instead of per-job settings
  const useAIForThisJob = globalUseAI;
  const enableParkingReminderForThisJob = globalEnableParking;
  const [reportData, setReportData] = useState({
    materialsUsed: '',
    workDescription: '',
    serviceCharged: '',
    customerSignature: '',
    customerRating: 10, // Rating from 1 to 10 (default 10)
    additionalNotes: '',
    usedSpareParts: [], // Add array for multiple spare parts
    additionalMaterials: [], // Additional materials: [{ name, price, quantity }]
    jobNotCompleted: false, // Checkbox: posao nije završen
    nextVisitNotes: '', // Napomena šta treba poneti sledeći put
    amortization: '', // Amortizacija (trošak vozila)
    signatureData: '', // Digital signature data (base64)
    underWarranty: false // Checkbox: aparat je bio u garanciji
  });
  const [showPrintModal, setShowPrintModal] = useState(false);
  
  const [workerReversi, setWorkerReversi] = useState([]);
  const [reversiLoading, setReversiLoading] = useState(false);
  const [reversiSearchTerm, setReversiSearchTerm] = useState('');
  const [selectedReversiParts, setSelectedReversiParts] = useState({}); // { reversId: { quantity: number, ...reversData } }
  const [selectedWithdrawnMaterials, setSelectedWithdrawnMaterials] = useState({}); // { movementId: { quantity, unitSellingPrice, ... } }
  const [selectedReversiCategoryId, setSelectedReversiCategoryId] = useState('');
  const [showReversiBox, setShowReversiBox] = useState(false);
  const [showWithdrawnBox, setShowWithdrawnBox] = useState(false);
  const [showAdditionalMaterialsBox, setShowAdditionalMaterialsBox] = useState(false);
  const [deviceCategories, setDeviceCategories] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [, setSelectionLoading] = useState(false);
  const initialCategoryId = job?.deviceCategoryId || job?.deviceTypeId;
  const [selectedCategoryId, setSelectedCategoryId] = useState(initialCategoryId ? initialCategoryId.toString() : '');
  const [selectedServiceId, setSelectedServiceId] = useState(job?.serviceId ? job.serviceId.toString() : '');
  const [selectedServicePrice, setSelectedServicePrice] = useState(job?.servicePrice ?? '');
  const [selectedServiceName, setSelectedServiceName] = useState(job?.serviceName || '');
  const [selectedServiceDuration, setSelectedServiceDuration] = useState(
    job?.serviceDurationMinutes ?? ''
  );
  const [selectedCategoryName, setSelectedCategoryName] = useState(job?.deviceCategoryName || '');
  const accumulatedTravelTimeRef = useRef(0);
  const accumulatedWorkTimeRef = useRef(0);
  const [showAIDiagnosisModal, setShowAIDiagnosisModal] = useState(false);
  const [aiDiagnosisData, setAiDiagnosisData] = useState(null);
  const [aiDiagnosisLoading, setAiDiagnosisLoading] = useState(false);

  const hasMeasuredDuration =
    (accumulatedTravelTimeRef.current || 0) > 0 ||
    (accumulatedWorkTimeRef.current || 0) > 0;

  // 🔐 BLOKADA: radnik ne sme da pokrene novi posao / novo putovanje dok ima aktivan stari posao
  const workerId = user?.result?._id;

  const activeJobForWorker = useMemo(() => {
    if (!workerId) return null;
    return (
      allJobs.find((j) => {
        if (!j) return false;
        // assignedTo može biti string ili objekat
        const assignedId =
          typeof j.assignedTo === 'object' && j.assignedTo !== null
            ? (j.assignedTo._id || j.assignedTo.id)
            : j.assignedTo;

        const isMine = assignedId && assignedId.toString() === workerId.toString();
        if (!isMine) return false;

        // Proveri aktivno putovanje: travelStartTime postoji ILI status je 'On Road'
        const hasActiveTravel = !!j.travelStartTime || j.status === 'On Road';
        // Proveri aktivni rad: workStartTime postoji ILI status je 'In Repair', 'In Progress', 'At Client', 'Diagnosing'
        const hasActiveWork = !!j.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(j.status);
        
        return hasActiveTravel || hasActiveWork;
      }) || null
    );
  }, [allJobs, workerId]);

  const hasOtherActiveJob =
    isWorker &&
    activeJobForWorker &&
    job?._id &&
    activeJobForWorker._id &&
    activeJobForWorker._id.toString() !== job._id.toString();

  // 🔐 PROVERA: Da li radnik ima aktivno putovanje ili posao (bilo gde - u bilo kom poslu)
  const hasActiveTravelOrWork = useMemo(() => {
    if (!isWorker || !workerId) return false;
    
    // PRVO: Proveri lokalno stanje (isTraveling, isWorking) za trenutni posao
    // Ovo je kritično jer se lokalno stanje ažurira pre nego što se Redux store ažurira
    if (isTraveling || isWorking) {
      console.log('🔐 BLOKADA: Lokalno stanje aktivno', { isTraveling, isWorking, jobId: job?._id });
      return true;
    }
    
    // DRUGO: Proveri localStorage za sve poslove (backup mehanizam)
    if (typeof window !== 'undefined') {
      const allStorageKeys = Object.keys(localStorage);
      const travelKeys = allStorageKeys.filter(key => key.startsWith('isTraveling_'));
      const workKeys = allStorageKeys.filter(key => key.startsWith('isWorking_'));
      
      // Proveri da li postoji aktivno putovanje ili rad u localStorage
      const hasActiveTravelInStorage = travelKeys.some(key => {
        const value = localStorage.getItem(key);
        return value === 'true';
      });
      const hasActiveWorkInStorage = workKeys.some(key => {
        const value = localStorage.getItem(key);
        return value === 'true';
      });
      
      if (hasActiveTravelInStorage || hasActiveWorkInStorage) {
        console.log('🔐 BLOKADA: Aktivno stanje u localStorage', { hasActiveTravelInStorage, hasActiveWorkInStorage });
        return true;
      }
    }
    
    // TREĆE: Proveri sve poslove dodeljene radniku iz Redux store-a
    const myJobs = allJobs.filter((j) => {
      if (!j) return false;
      const assignedId =
        typeof j.assignedTo === 'object' && j.assignedTo !== null
          ? (j.assignedTo._id || j.assignedTo.id)
          : j.assignedTo;
      return assignedId && assignedId.toString() === workerId.toString();
    });
    
    // Proveri da li postoji aktivno putovanje ili posao
    const hasActiveInJobs = myJobs.some((j) => {
      if (!j) return false;
      // Aktivno putovanje: travelStartTime postoji ILI status je 'On Road'
      const hasActiveTravel = !!j.travelStartTime || j.status === 'On Road';
      // Aktivni posao: workStartTime postoji ILI status je aktivni
      const hasActiveWork = !!j.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(j.status);
      
      return hasActiveTravel || hasActiveWork;
    });
    
    if (hasActiveInJobs) {
      console.log('🔐 BLOKADA: Aktivno stanje u Redux store', { myJobsCount: myJobs.length });
    }
    
    return hasActiveInJobs;
  }, [allJobs, workerId, isWorker, isTraveling, isWorking, job]);

  // 🔐 PROVERA: Da li trenutni posao ima aktivno putovanje ili posao
  const currentJobHasActiveTravelOrWork = useMemo(() => {
    if (!job) return false;
    // Proveri i lokalno stanje (isTraveling, isWorking) i podatke iz baze (travelStartTime, workStartTime)
    const hasActiveTravel = isTraveling || !!job.travelStartTime || job.status === 'On Road';
    const hasActiveWork = isWorking || !!job.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(job.status);
    return hasActiveTravel || hasActiveWork;
  }, [job, isTraveling, isWorking]);

  // Log when AI modal state changes
  useEffect(() => {
    console.log('🤖 [AI DIAGNOSIS] Modal state changed:', {
      isOpen: showAIDiagnosisModal,
      jobId: job?._id,
      loading: aiDiagnosisLoading,
      hasData: !!aiDiagnosisData,
      timestamp: new Date().toISOString()
    });
  }, [showAIDiagnosisModal, aiDiagnosisLoading, aiDiagnosisData, job?._id]);

  // Log when parking modal state changes
  useEffect(() => {
    console.log('🅿️ [PARKING MODAL] Modal state changed:', {
      showParkingModal,
      jobId: job?._id,
      willRender: showParkingModal === true,
      timestamp: new Date().toISOString()
    });
  }, [showParkingModal, job?._id]);

  // Load accumulated times and active timers from database and localStorage on mount
  // VAŽNO: Ovo radimo samo za radnika (worker), ne i za admina (company),
  // da admin nema svoj nezavisan tajmer koji nastavlja da "curi".
  useEffect(() => {
    if (!isWorker) {
      // Admin / owner samo gleda podatke koje dobija preko WebSocketa (StatusView, kalendar, itd.)
      // i ne treba da pokreće sopstvene intervale/tajmere ovde.
      return;
    }

    const loadTimerState = async () => {
      const jobId = job?._id;
      if (!jobId || !user?.token) return;

      const realNow = Date.now();
      const realNowISO = new Date().toISOString();
      
      // LOG: Load timer state - POVRATAK
      console.log('📥 LOAD TIMER STATE - POVRATAK:', {
        jobId,
        clientName: job?.clientName,
        realNow: realNowISO,
        realNowTimestamp: realNow,
        timestamp: new Date().toISOString()
      });

      try {
        // First, try to load from database (this is the source of truth)
        const response = await axios.get(
          `${API_BASE_URL}/api/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        
        const jobData = response.data;
        
        // Load accumulated times from database FIRST, before logging
        // IMPORTANT: Always use database values, even if they are 0 (reset was successful)
        accumulatedTravelTimeRef.current = jobData.accumulatedTravelTime !== undefined && jobData.accumulatedTravelTime !== null 
          ? jobData.accumulatedTravelTime 
          : 0;
        accumulatedWorkTimeRef.current = jobData.accumulatedWorkTime !== undefined && jobData.accumulatedWorkTime !== null 
          ? jobData.accumulatedWorkTime 
          : 0;
        
        // Update local state to match database values
        setTravelTime(accumulatedTravelTimeRef.current);
        setWorkTime(accumulatedWorkTimeRef.current);
        
        // LOG: Load timer state (after loading from DB)
        console.log('📥 LOAD TIMER STATE:', {
          jobId,
          clientName: jobData?.clientName,
          realNow: realNowISO,
          realNowTimestamp: realNow,
          jobData_accumulatedTravelTime: jobData.accumulatedTravelTime,
          jobData_accumulatedWorkTime: jobData.accumulatedWorkTime,
          jobData_travelStartTime: jobData.travelStartTime,
          jobData_travelStartTimeISO: jobData.travelStartTime ? new Date(jobData.travelStartTime).toISOString() : null,
          jobData_workStartTime: jobData.workStartTime,
          jobData_workStartTimeISO: jobData.workStartTime ? new Date(jobData.workStartTime).toISOString() : null,
          jobData_status: jobData.status,
          accumulatedTravelTimeRef_afterLoad: accumulatedTravelTimeRef.current,
          accumulatedWorkTimeRef_afterLoad: accumulatedWorkTimeRef.current,
          timestamp: new Date().toISOString()
        });
        
        // LOG: After load
        console.log('📥 LOAD TIMER STATE (AFTER LOAD):', {
          jobId,
          accumulatedTravelTimeRef_after: accumulatedTravelTimeRef.current,
          accumulatedWorkTimeRef_after: accumulatedWorkTimeRef.current,
          timestamp: new Date().toISOString()
        });
        
        // Also save to localStorage for backup
        localStorage.setItem(`travelTime_${jobId}`, accumulatedTravelTimeRef.current.toString());
        localStorage.setItem(`workTime_${jobId}`, accumulatedWorkTimeRef.current.toString());
        
        // Check if timers are active based on status and start times
        const now = Date.now();
        
        // Check travel timer
        if (jobData.travelStartTime) {
          const travelStart = new Date(jobData.travelStartTime).getTime();
          const travelStartISO = new Date(travelStart).toISOString();
          const elapsed = Math.floor((now - travelStart) / 1000);
          const isTimerValid = now - travelStart < 24 * 60 * 60 * 1000 && jobData.status === 'On Road';
          
          // LOG: Resume travel timer
          console.log('▶️ RESUME TRAVEL TIMER:', {
            jobId,
            clientName: jobData?.clientName,
            realNow: new Date(now).toISOString(),
            realNowTimestamp: now,
            travelStart: travelStart,
            travelStartISO: travelStartISO,
            elapsed,
            elapsedMinutes: Math.floor(elapsed / 60),
            accumulatedTravelTimeRef: accumulatedTravelTimeRef.current,
            accumulatedTravelTimeRefMinutes: Math.floor(accumulatedTravelTimeRef.current / 60),
            isTimerValid,
            status: jobData.status,
            calculatedTotal: accumulatedTravelTimeRef.current + elapsed,
            calculatedTotalMinutes: Math.floor((accumulatedTravelTimeRef.current + elapsed) / 60),
            timestamp: new Date().toISOString()
          });
          
          // Check if timer is still valid (not too old - e.g., within 24 hours)
          if (isTimerValid) {
            setIsTraveling(true);
            setTravelStartTime(travelStart);
            // Calculate elapsed time from saved start time
            const currentTravelTime = accumulatedTravelTimeRef.current + elapsed;
            setTravelTime(currentTravelTime);
            
            console.log('▶️ RESUME TRAVEL TIMER - SET STATE:', {
              jobId,
              travelStartTime: travelStart,
              currentTravelTime,
              currentTravelTimeMinutes: Math.floor(currentTravelTime / 60),
              timestamp: new Date().toISOString()
            });
            
            // Also save to localStorage for backup
            localStorage.setItem(`isTraveling_${jobId}`, 'true');
            localStorage.setItem(`travelStartTime_${jobId}`, travelStart.toString());
          } else {
            // Timer is too old or status changed, clear it
            console.log('▶️ RESUME TRAVEL TIMER - TIMER INVALID (clearing):', {
              jobId,
              reason: now - travelStart >= 24 * 60 * 60 * 1000 ? 'too_old' : 'status_changed',
              status: jobData.status,
              timestamp: new Date().toISOString()
            });
            setIsTraveling(false);
            setTravelStartTime(null);
            localStorage.removeItem(`isTraveling_${jobId}`);
            localStorage.removeItem(`travelStartTime_${jobId}`);
          }
        } else {
          setIsTraveling(false);
          setTravelStartTime(null);
        }
        
        // Check work timer
        if (jobData.workStartTime) {
          const workStart = new Date(jobData.workStartTime).getTime();
          const workStartISO = new Date(workStart).toISOString();
          // Check if timer is still valid (not too old - e.g., within 24 hours)
          if (now - workStart < 24 * 60 * 60 * 1000 && (jobData.status === 'In Repair' || jobData.status === 'In Progress')) {
            // Calculate elapsed time from saved start time
            const elapsed = Math.floor((now - workStart) / 1000);
            const currentWorkTime = accumulatedWorkTimeRef.current + elapsed;
            
            // LOG: Resume work timer
            console.log('▶️ RESUME WORK TIMER:', {
              jobId,
              clientName: jobData?.clientName,
              realNow: new Date(now).toISOString(),
              realNowTimestamp: now,
              workStart: workStart,
              workStartISO: workStartISO,
              elapsed,
              accumulatedWorkTimeRef: accumulatedWorkTimeRef.current,
              currentWorkTime,
              timestamp: new Date().toISOString()
            });
            
            setIsWorking(true);
            setWorkStartTime(workStart);
            setWorkTime(currentWorkTime);
            
            // Also save to localStorage for backup
            localStorage.setItem(`isWorking_${jobId}`, 'true');
            localStorage.setItem(`workStartTime_${jobId}`, workStart.toString());
          } else {
            // Timer is too old or status changed, clear it
            console.log('⏹️ CLEAR WORK TIMER (too old or status changed):', {
              jobId,
              workStart,
              workStartISO: new Date(workStart).toISOString(),
              now,
              nowISO: new Date(now).toISOString(),
              ageInHours: (now - workStart) / (1000 * 60 * 60),
              status: jobData.status,
              timestamp: new Date().toISOString()
            });
            setIsWorking(false);
            setWorkStartTime(null);
            localStorage.removeItem(`isWorking_${jobId}`);
            localStorage.removeItem(`workStartTime_${jobId}`);
          }
        } else {
          setIsWorking(false);
          setWorkStartTime(null);
        }
      } catch (error) {
        console.error('Error loading timer state from database:', error);
        
        // Fallback to localStorage if database fails
        const savedTravelTime = localStorage.getItem(`travelTime_${jobId}`);
        const savedWorkTime = localStorage.getItem(`workTime_${jobId}`);
        
        if (savedTravelTime) {
          accumulatedTravelTimeRef.current = parseInt(savedTravelTime);
        }
        if (savedWorkTime) {
          accumulatedWorkTimeRef.current = parseInt(savedWorkTime);
        }
        
        // Load active timer states from localStorage
        const savedIsTraveling = localStorage.getItem(`isTraveling_${jobId}`);
        const savedIsWorking = localStorage.getItem(`isWorking_${jobId}`);
        const savedTravelStartTime = localStorage.getItem(`travelStartTime_${jobId}`);
        const savedWorkStartTime = localStorage.getItem(`workStartTime_${jobId}`);
        
        if (savedIsTraveling === 'true' && savedTravelStartTime) {
          const startTime = parseInt(savedTravelStartTime);
          const now = Date.now();
          if (now - startTime < 24 * 60 * 60 * 1000) {
            setIsTraveling(true);
            setTravelStartTime(startTime);
            const elapsed = Math.floor((now - startTime) / 1000);
            const currentTravelTime = accumulatedTravelTimeRef.current + elapsed;
            setTravelTime(currentTravelTime);
          }
        }
        
        if (savedIsWorking === 'true' && savedWorkStartTime) {
          const startTime = parseInt(savedWorkStartTime);
          const now = Date.now();
          if (now - startTime < 24 * 60 * 60 * 1000) {
            setIsWorking(true);
            setWorkStartTime(startTime);
            const elapsed = Math.floor((now - startTime) / 1000);
            const currentWorkTime = accumulatedWorkTimeRef.current + elapsed;
            setWorkTime(currentWorkTime);
          }
        }
      }
    };

    loadTimerState();
  }, [job?._id, job?.clientName, user?.token, isWorker]);

  // Save accumulated times to localStorage
  const saveAccumulatedTimes = useCallback(() => {
    const jobId = job?._id;
    if (jobId) {
      try {
        localStorage.setItem(`travelTime_${jobId}`, accumulatedTravelTimeRef.current.toString());
        localStorage.setItem(`workTime_${jobId}`, accumulatedWorkTimeRef.current.toString());
        
        // Also save timer states for offline recovery
        if (isTraveling && travelStartTime) {
          localStorage.setItem(`isTraveling_${jobId}`, 'true');
          localStorage.setItem(`travelStartTime_${jobId}`, travelStartTime.toString());
        }
        if (isWorking && workStartTime) {
          localStorage.setItem(`isWorking_${jobId}`, 'true');
          localStorage.setItem(`workStartTime_${jobId}`, workStartTime.toString());
        }
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }
  }, [job?._id, isTraveling, travelStartTime, isWorking, workStartTime]);

  // Save to localStorage before page unload/refresh (CRITICAL for offline scenarios)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Save accumulated times before page closes/refreshes
      saveAccumulatedTimes();
    };

    const handleVisibilityChange = () => {
      // Save when page becomes hidden (user switches tabs, minimizes, etc.)
      if (document.visibilityState === 'hidden') {
        saveAccumulatedTimes();
      }
    };

    // Save periodically every second when timers are active (extra safety for offline scenarios)
    let periodicSaveInterval;
    if ((isTraveling || isWorking) && job?._id) {
      periodicSaveInterval = setInterval(() => {
        saveAccumulatedTimes();
      }, 1000); // Save every second for extra safety when timers are active
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (periodicSaveInterval) {
        clearInterval(periodicSaveInterval);
      }
    };
  }, [saveAccumulatedTimes, isTraveling, isWorking, job?._id]);

  useEffect(() => {
    // Initialize status from job
    if (job?.status) {
      // Handle different status formats
      let normalizedStatus = job.status.toLowerCase();
      if (normalizedStatus === 'in pending') {
        normalizedStatus = 'pending';
      }
      setCurrentStatus(normalizedStatus);
    }
  }, [job]);

  // Admin (company): kada otvori završen posao, učitaj accumulated vremena iz job-a da štampa i prikaz budu tačni
  useEffect(() => {
    if (!isWorker && job?.status === 'Finished' && job?._id) {
      const travel = Number(job.accumulatedTravelTime) || 0;
      const work = Number(job.accumulatedWorkTime) || 0;
      accumulatedTravelTimeRef.current = travel;
      accumulatedWorkTimeRef.current = work;
      setTravelTime(travel);
      setWorkTime(work);
    }
  }, [isWorker, job?._id, job?.status, job?.accumulatedTravelTime, job?.accumulatedWorkTime]);

useEffect(() => {
  const updatedCategoryId = job?.deviceCategoryId || job?.deviceTypeId;
  setSelectedCategoryId(updatedCategoryId ? updatedCategoryId.toString() : '');
  setSelectedServiceId(job?.serviceId ? job.serviceId.toString() : '');
  setSelectedServicePrice(job?.servicePrice ?? '');
  setSelectedServiceDuration(job?.serviceDurationMinutes ?? '');
  setSelectedServiceName(job?.serviceName || '');
  setSelectedCategoryName(job?.deviceCategoryName || '');
}, [
  job?.deviceTypeId,
  job?.deviceCategoryId,
  job?.serviceId,
  job?.servicePrice,
  job?.serviceDurationMinutes,
  job?.serviceName,
  job?.deviceCategoryName,
]);

  // Load spare parts from Redux store
  useEffect(() => {
    if (user?.token) {
      dispatch(getSpareParts());
    }
  }, [dispatch, user?.token]);

  // Load worker reversi (consumed materials from receipts)
  useEffect(() => {
    const fetchWorkerReversi = async () => {
      if (!user?.token || !user?.result?._id || sparePartsLoading) return;
      
      setReversiLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/inventory/reversi`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        
        // Filter reversi for current worker that are not returned
        const workerId = user.result._id;
        console.log('Fetching reversi for worker:', workerId);
        console.log('All reversi from API:', response.data);
        
        const filtered = (response.data || []).filter(revers => {
          const reversWorkerId = typeof revers.workerId === 'object' && revers.workerId !== null
            ? (revers.workerId._id || revers.workerId.id)
            : revers.workerId;
          const matches = reversWorkerId?.toString() === workerId?.toString() && !revers.returned;
          console.log('Revers check:', { reversWorkerId, workerId, returned: revers.returned, matches });
          return matches;
        });
        
        console.log('Filtered reversi for worker:', filtered);
        
        // Populate with part details (rezervni delovi ili magacin)
        const populated = filtered.map((revers) => {
          const sourceType = revers.sourceType || (revers.inventoryItemId ? 'INVENTORY_ITEM' : 'SPARE_PART');

          if (revers.item) {
            const part = {
              _id: revers.item._id,
              name: revers.item.name,
              code: revers.item.code || '',
              price: revers.item.price || 0,
              category: revers.item.category || '',
              deviceCategory: revers.item.deviceCategory,
            };
            return {
              ...revers,
              sourceType,
              sparePart: part,
              totalCost: (part.price || 0) * revers.quantity,
            };
          }

          if (sourceType === 'INVENTORY_ITEM') {
            const inv = typeof revers.inventoryItemId === 'object' && revers.inventoryItemId !== null
              ? revers.inventoryItemId
              : null;
            const part = inv
              ? {
                  _id: inv._id,
                  name: inv.name,
                  code: inv.itemCode || '',
                  price: inv.sellingPrice || inv.averageCost || inv.purchasePrice || 0,
                  category: inv.category || '',
                }
              : null;
            return {
              ...revers,
              sourceType,
              sparePart: part,
              totalCost: (part?.price || 0) * revers.quantity,
            };
          }

          const sparePartId = typeof revers.sparePartId === 'object' && revers.sparePartId !== null
            ? (revers.sparePartId._id || revers.sparePartId.id)
            : revers.sparePartId;

          const sparePart = spareParts.find((p) => {
            const partId = (p._id || p.id)?.toString();
            return partId === sparePartId?.toString();
          });

          const finalSparePart = sparePart || (typeof revers.sparePartId === 'object' && revers.sparePartId !== null ? revers.sparePartId : null);
          const partPrice = finalSparePart?.price || (typeof revers.sparePartId === 'object' && revers.sparePartId !== null ? revers.sparePartId.price : 0) || 0;

          return {
            ...revers,
            sourceType,
            sparePart: finalSparePart || (typeof revers.sparePartId === 'object' && revers.sparePartId !== null ? revers.sparePartId : null),
            totalCost: partPrice * revers.quantity,
          };
        });
        
        setWorkerReversi(populated);
      } catch (error) {
        console.error('Error fetching worker reversi:', error);
      } finally {
        setReversiLoading(false);
      }
    };

    fetchWorkerReversi();
  }, [user?.token, user?.result?._id, spareParts, sparePartsLoading]);

  useEffect(() => {
    let isMounted = true;
    const loadHierarchy = async () => {
      if (!user?.token) return;
      setSelectionLoading(true);
      try {
        const [categoriesRes, servicesRes] = await Promise.all([
          fetchDeviceCategories(),
          fetchServicesList()
        ]);
        if (!isMounted) return;
        setDeviceCategories(Array.isArray(categoriesRes?.data) ? categoriesRes.data : []);
        setServiceTemplates(Array.isArray(servicesRes?.data) ? servicesRes.data : []);
      } catch (error) {
        console.error('Greška pri učitavanju usluga za radnika:', error);
      } finally {
        if (isMounted) {
          setSelectionLoading(false);
        }
      }
    };

    loadHierarchy();
    return () => {
      isMounted = false;
    };
  }, [user?.token, setSelectionLoading]);

  const updateTravelTimer = useCallback(() => {
    if (isTraveling && travelStartTime) {
      const elapsed = Math.floor((Date.now() - travelStartTime) / 1000);
      const total = accumulatedTravelTimeRef.current + elapsed;
      setTravelTime(total);
    }
  }, [isTraveling, travelStartTime]);

  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.id) return value.id.toString();
      if (value._id) return value._id.toString();
    }
    return value.toString();
  };

  const workerSelectedCategory = useMemo(() => {
    const normalized = normalizeId(selectedCategoryId);
    if (!normalized) {
      if (selectedCategoryName) {
        return { name: selectedCategoryName, color: '#374151' };
      }
      return null;
    }
    return deviceCategories.find(category => normalizeId(category.id || category._id) === normalized) || (selectedCategoryName ? { name: selectedCategoryName, color: '#374151' } : null);
  }, [deviceCategories, selectedCategoryId, selectedCategoryName]);

  // Ako posao već ima upisanu kategoriju prilikom prijave kvara,
  // automatski je izaberi u dropdown-u za usluge kada se liste kategorija učitaju.
  useEffect(() => {
    if (selectedCategoryId) return;
    if (!job?.deviceCategoryName) return;
    if (!Array.isArray(deviceCategories) || deviceCategories.length === 0) return;

    const match = deviceCategories.find(
      (category) => category.name === job.deviceCategoryName
    );

    if (match) {
      const normalizedId = normalizeId(match.id || match._id);
      setSelectedCategoryId(normalizedId);
      setSelectedCategoryName(match.name);
    }
  }, [selectedCategoryId, job?.deviceCategoryName, deviceCategories]);

  // Podrazumevana kategorija za delove iz reversa – ista kao na prijavi kvara.
  useEffect(() => {
    if (selectedReversiCategoryId) return;
    if (!job?.deviceCategoryName) return;
    if (!Array.isArray(deviceCategories) || deviceCategories.length === 0) return;

    const match = deviceCategories.find(
      (category) => category.name === job.deviceCategoryName
    );

    if (match) {
      const normalizedId = normalizeId(match.id || match._id);
      console.log('🔧 [Reversi] Default kategorija za delove iz reversa postavljena iz posla:', {
        jobCategoryName: job.deviceCategoryName,
        normalizedId,
        matchCategory: match
      });
      setSelectedReversiCategoryId(normalizedId);
    }
  }, [selectedReversiCategoryId, job?.deviceCategoryName, deviceCategories]);

  // Log kada radnik ručno promeni kategoriju za delove iz reversa
  useEffect(() => {
    if (!selectedReversiCategoryId) {
      console.log('🔧 [Reversi] Nema izabrane kategorije za delove iz reversa (sve kategorije).');
      return;
    }
    const normalized = normalizeId(selectedReversiCategoryId);
    const match = deviceCategories.find(
      (category) => normalizeId(category.id || category._id) === normalized
    );
    console.log('🔧 [Reversi] Ručno izabrana kategorija za delove iz reversa:', {
      selectedReversiCategoryId,
      normalized,
      categoryFound: match
    });
  }, [selectedReversiCategoryId, deviceCategories]);

  const workerSelectedService = useMemo(() => {
    if (!selectedServiceId) return null;
    return serviceTemplates.find((service) => normalizeId(service.id || service._id) === selectedServiceId) || null;
  }, [serviceTemplates, selectedServiceId]);

  const selectedReversiCategory = useMemo(() => {
    if (!selectedReversiCategoryId) return null;
    const normalized = normalizeId(selectedReversiCategoryId);
    return (
      deviceCategories.find(
        (cat) => normalizeId(cat.id || cat._id) === normalized
      ) || null
    );
  }, [selectedReversiCategoryId, deviceCategories]);

  // Lista delova na reversu filtrirana po kategoriji i pretrazi (za prikaz u "Potrošeni materijal iz reversa")
  // Gradi se iz workerReversi (API sa populate category/deviceCategory), ne iz Redux spareParts – da delovi uvek budu oni koje radnik ima na reversu
  const filteredReversiPartsList = useMemo(() => {
    if (workerReversi.length === 0) return [];
    const withQuantity = workerReversi.filter((r) => r.quantity > 0);
    const getPartFromRevers = (revers) => {
      if (revers.item) {
        return {
          _id: revers.item._id,
          name: revers.item.name,
          code: revers.item.code || '',
          price: revers.item.price || 0,
          category: revers.item.category || '',
          deviceCategory: revers.item.deviceCategory,
        };
      }
      const fromMerged = revers.sparePart || null;
      if (fromMerged) return fromMerged;
      const fromPopulate = typeof revers.sparePartId === 'object' && revers.sparePartId !== null ? revers.sparePartId : null;
      if (fromPopulate) return fromPopulate;
      if (typeof revers.inventoryItemId === 'object' && revers.inventoryItemId !== null) {
        const inv = revers.inventoryItemId;
        return {
          _id: inv._id,
          name: inv.name,
          code: inv.itemCode || '',
          price: inv.sellingPrice || inv.averageCost || inv.purchasePrice || 0,
          category: inv.category || '',
        };
      }
      return null;
    };
    let list = withQuantity;
    if (selectedReversiCategory && (selectedReversiCategory.name || selectedReversiCategory.id || selectedReversiCategory._id)) {
      const selectedCategoryName = (selectedReversiCategory.name || '').trim().toLowerCase();
      const selectedCategoryIdNorm = normalizeId(selectedReversiCategoryId || selectedReversiCategory.id || selectedReversiCategory._id);
      list = withQuantity.filter((revers) => {
        const part = getPartFromRevers(revers);
        if (!part) return false;
        const partCategoryName = (part.category || '').trim().toLowerCase();
        const partDeviceCat = part.deviceCategory;
        const partCategoryId = partDeviceCat
          ? normalizeId(typeof partDeviceCat === 'object' ? (partDeviceCat._id || partDeviceCat.id) : partDeviceCat)
          : '';
        const matchByName = partCategoryName && selectedCategoryName && partCategoryName === selectedCategoryName;
        const matchById = partCategoryId && selectedCategoryIdNorm && partCategoryId === selectedCategoryIdNorm;
        return matchByName || matchById;
      });
    }
    if (reversiSearchTerm) {
      const searchLower = reversiSearchTerm.toLowerCase();
      list = list.filter((revers) => {
        const part = getPartFromRevers(revers);
        if (!part) return false;
        const name = (part.name || '').toLowerCase();
        const code = (part.code || '').toLowerCase();
        return name.includes(searchLower) || code.includes(searchLower);
      });
    }
    return list.map((revers) => ({
      revers,
      part: getPartFromRevers(revers) || { name: 'Nepoznat deo', code: '', price: 0 }
    }));
  }, [workerReversi, selectedReversiCategory, selectedReversiCategoryId, reversiSearchTerm]);

  const warrantyServiceOptions = useMemo(() => {
    if (!Array.isArray(serviceTemplates) || serviceTemplates.length === 0) return [];

    const normalizedCategoryId = selectedCategoryId ? normalizeId(selectedCategoryId) : null;

    // Ako kategorija nije izabrana, ne prikazuj listu usluga – prvo se bira kategorija.
    if (!normalizedCategoryId) {
      return [];
    }

    const filtered = serviceTemplates.filter((service) => {
      const svcCategoryId = service.deviceCategoryId || service.deviceCategory?.id;
      if (!svcCategoryId) return false;
      return normalizeId(svcCategoryId) === normalizedCategoryId;
    });

    return filtered;
  }, [serviceTemplates, selectedCategoryId]);

  const persistJobSelections = useCallback(async () => {
    if (!job?._id) return;
    if (!user?.token) return;

    const hasCategoryUpdate = !!selectedCategoryId;
    const hasServiceUpdate = !!selectedServiceId;
    if (!hasCategoryUpdate && !hasServiceUpdate) return;

    const servicePriceValue =
      selectedServicePrice === '' || selectedServicePrice === null || selectedServicePrice === undefined
        ? null
        : Number(selectedServicePrice);

    const serviceDurationValue =
      selectedServiceDuration === '' || selectedServiceDuration === null || selectedServiceDuration === undefined
        ? null
        : Number(selectedServiceDuration);

    const normalizedCategoryId = selectedCategoryId ? normalizeId(selectedCategoryId) : null;
    const resolvedCategoryName =
      workerSelectedCategory?.name || selectedCategoryName || job?.deviceCategoryName || '';

    const payload = {
      deviceTypeId: normalizedCategoryId || null,
      deviceType: resolvedCategoryName || job?.deviceType || '',
      deviceCategoryId: normalizedCategoryId || job?.deviceCategoryId || null,
      deviceCategoryName: resolvedCategoryName,
      serviceId: selectedServiceId || null,
      serviceName: selectedServiceName || workerSelectedService?.name || job?.serviceName || '',
      servicePrice: servicePriceValue !== null && Number.isFinite(servicePriceValue) ? servicePriceValue : null,
      serviceDurationMinutes:
        serviceDurationValue !== null && Number.isFinite(serviceDurationValue) ? serviceDurationValue : null,
    };

    try {
      await axios.put(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        payload,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
    } catch (error) {
      console.error('Greška pri ažuriranju usluge/tipa uređaja za posao:', error);
    }
  }, [job?._id, user?.token, selectedCategoryId, selectedServiceId, selectedServicePrice, selectedServiceDuration, workerSelectedCategory, workerSelectedService, selectedCategoryName, selectedServiceName, job?.deviceType, job?.serviceName, job?.deviceCategoryId, job?.deviceCategoryName]);

  const handleWarrantyServiceSelect = (event) => {
    const newServiceId = event.target.value;
    setSelectedServiceId(newServiceId);

    if (!newServiceId) {
      return;
    }

    const selected = serviceTemplates.find(
      (service) => normalizeId(service.id || service._id) === newServiceId
    );

    if (!selected) {
      return;
    }

    const priceFromTemplate =
      selected.price !== undefined && selected.price !== null
        ? selected.price
        : null;

    const durationFromTemplate =
      selected.durationMinutes !== undefined && selected.durationMinutes !== null
        ? selected.durationMinutes
        : null;

    if (selected.name) {
      setSelectedServiceName(selected.name);
    }

    if (priceFromTemplate !== null) {
      setSelectedServicePrice(priceFromTemplate);
      setReportData((prev) => {
        // Ako je aparat u garanciji, podrazumevana naplata prema klijentu je 0,
        // ali korisnik uvek može ručno da promeni cenu u polju "Naplaćena usluga".
        const isWarranty = prev.underWarranty || !!job?.hasWarranty;
        return {
          ...prev,
          serviceCharged: isWarranty ? '0' : String(priceFromTemplate),
        };
      });
    }

    if (durationFromTemplate !== null) {
      setSelectedServiceDuration(durationFromTemplate);
    }
  };

  const updateWorkTimer = useCallback(() => {
    if (isWorking && workStartTime) {
      const elapsed = Math.floor((Date.now() - workStartTime) / 1000);
      const total = accumulatedWorkTimeRef.current + elapsed;
      setWorkTime(total);
    }
  }, [isWorking, workStartTime]);

  // Timer effect for travel time - optimizovano da ne pravi nepotrebne re-renderovanja
  useEffect(() => {
    let interval;
    let saveInterval;
    if (isTraveling && travelStartTime) {
      interval = setInterval(updateTravelTimer, 1000);
      
      // Periodically save timer data to database and send via WebSocket (every 5 seconds)
      // IMPORTANT: Only save accumulatedTravelTimeRef.current, NOT accumulatedTravelTimeRef.current + elapsed
      // The elapsed time is calculated dynamically when loading, so we don't want to double-count it
      saveInterval = setInterval(async () => {
        const now = new Date();
        const realNow = Date.now();
        
        // ALWAYS save to localStorage first (even if offline) - CRITICAL for offline scenarios
        // This ensures data is preserved even if internet connection is lost
        saveAccumulatedTimes();
        
        // Save to database - ONLY save the accumulated time, NOT accumulated + elapsed
        // The elapsed time will be calculated dynamically when the timer is loaded
        try {
          const token = user?.token;
          if (token && job?._id) {
            await axios.patch(
              `${API_BASE_URL}/api/jobs/${job._id}`,
              {
                travelStartTime: new Date(travelStartTime),
                accumulatedTravelTime: accumulatedTravelTimeRef.current
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (error) {
          console.error('Error saving travel timer to database:', error);
          // Even if database save fails, localStorage is already saved above
        }
        
        // Send via WebSocket for real-time sync (only if online)
        try {
          emitJobStatusUpdate({
            jobId: job._id,
            status: job.status,
            workerId: user?.result?._id,
            workerName: user?.result?.name,
            timestamp: now.toISOString(),
            travelStartTime: new Date(travelStartTime).toISOString(), // Convert to ISO string
            workStartTime: workStartTime ? new Date(workStartTime).toISOString() : (job.workStartTime ? new Date(job.workStartTime).toISOString() : null),
            accumulatedTravelTime: accumulatedTravelTimeRef.current,
            accumulatedWorkTime: accumulatedWorkTimeRef.current
          });
        } catch (wsError) {
          console.error('Error sending WebSocket update:', wsError);
          // WebSocket errors are not critical - localStorage is saved
        }
      }, 2000); // Save every 2 seconds - CRITICAL for mobile devices where localStorage can be cleared
    } else {
      // Ako nije traveling, postavi vreme na akumulirano
      setTravelTime(accumulatedTravelTimeRef.current);
    }
    return () => {
      clearInterval(interval);
      clearInterval(saveInterval);
    };
  }, [isTraveling, travelStartTime, updateTravelTimer, job, user, emitJobStatusUpdate, workStartTime]);

  // Timer effect for work time - optimizovano da ne pravi nepotrebne re-renderovanja
  useEffect(() => {
    let interval;
    let saveInterval;
    if (isWorking && workStartTime) {
      interval = setInterval(updateWorkTimer, 1000);
      
      // Periodically save timer data to database and send via WebSocket (every 5 seconds)
      // IMPORTANT: Only save accumulatedWorkTimeRef.current, NOT accumulatedWorkTimeRef.current + elapsed
      // The elapsed time is calculated dynamically when loading, so we don't want to double-count it
      saveInterval = setInterval(async () => {
        const now = new Date();
        
        // ALWAYS save to localStorage first (even if offline) - this is critical for offline scenarios
        saveAccumulatedTimes();
        
        // Save to database - ONLY save the accumulated time, NOT accumulated + elapsed
        // The elapsed time will be calculated dynamically when the timer is loaded
        try {
          const token = user?.token;
          if (token && job?._id) {
            await axios.patch(
              `${API_BASE_URL}/api/jobs/${job._id}`,
              {
                workStartTime: new Date(workStartTime),
                accumulatedWorkTime: accumulatedWorkTimeRef.current
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (error) {
          console.error('Error saving work timer to database:', error);
          // Even if database save fails, localStorage is already saved above
        }
        
        // Send via WebSocket for real-time sync (only if online)
        try {
          emitJobStatusUpdate({
            jobId: job._id,
            status: job.status,
            workerId: user?.result?._id,
            workerName: user?.result?.name,
            timestamp: now.toISOString(),
            travelStartTime: travelStartTime ? new Date(travelStartTime).toISOString() : (job.travelStartTime ? new Date(job.travelStartTime).toISOString() : null),
            workStartTime: new Date(workStartTime).toISOString(), // Convert to ISO string
            accumulatedTravelTime: accumulatedTravelTimeRef.current,
            accumulatedWorkTime: accumulatedWorkTimeRef.current
          });
        } catch (wsError) {
          console.error('Error sending WebSocket update:', wsError);
          // WebSocket errors are not critical - localStorage is saved
        }
      }, 2000); // Save every 2 seconds - CRITICAL for mobile devices where localStorage can be cleared
    } else {
      // Ako nije working, postavi vreme na akumulirano
      setWorkTime(accumulatedWorkTimeRef.current);
    }
    return () => {
      clearInterval(interval);
      clearInterval(saveInterval);
    };
  }, [isWorking, workStartTime, updateWorkTimer, job, user, emitJobStatusUpdate, travelStartTime]);

  // Update worker status without changing job status
  const updateWorkerStatusOnly = useCallback(async (workerStatus, notes = '') => {
    try {
      const token = user?.token;
      if (!token || !user?.result?._id) {
        console.warn('Cannot update worker status: missing token or user ID');
        return;
      }
      
      await axios.put(
        `${API_BASE_URL}/api/worker/${user.result._id}/status`,
        {
          status: workerStatus,
          currentJobId: job._id,
          location: job.clientAddress || 'Nepoznata adresa',
          notes: notes || `Posao: ${job.clientName || job._id}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      emitWorkerStatusUpdate({
        workerId: user.result._id,
        status: workerStatus,
        currentJobId: job._id,
        location: job.clientAddress || '',
        notes: notes || ''
      });
      
      console.log(`✅ Worker status updated to: ${workerStatus}`);
    } catch (error) {
      console.error('Error updating worker status:', error);
      // Don't throw - allow timers to continue even if status update fails
    }
  }, [user, job, emitWorkerStatusUpdate]);

  const promptClientRating = useCallback(async (ratingValue = null) => {
    if (typeof window === 'undefined') {
      console.log('⚠️ promptClientRating: window not available');
      return;
    }

    // Helper function to normalize phone numbers for comparison
    const normalizePhone = (phone) => {
      if (!phone) return '';
      // Remove all non-digit characters except +
      let normalized = phone.replace(/[^\d+]/g, '');
      // If starts with +, keep it; otherwise assume it's a local number
      // Extract only digits after + for comparison
      if (normalized.startsWith('+')) {
        normalized = normalized.slice(1); // Remove + for comparison
      }
      return normalized;
    };

    // Try to find client ID - first check if job has clientId, otherwise find by phone or name
    let clientId = job?.clientId;
    
    if (!clientId) {
      try {
        // Fetch all clients
        const response = await axios.get(
          `${API_BASE_URL}/api/client`,
          { headers: { Authorization: `Bearer ${user?.token}` } }
        );
        const clients = response.data || [];
        
        // Normalize job phone for comparison
        const jobPhoneNormalized = normalizePhone(job?.clientPhone || '');
        const jobNameNormalized = (job?.clientName || '').toLowerCase().trim();
        
        console.log('🔍 Searching for client:', {
          jobPhone: job?.clientPhone,
          jobPhoneNormalized,
          jobName: job?.clientName,
          jobNameNormalized,
          totalClients: clients.length
        });
        
        // Try to find by normalized phone number
        let foundClient = null;
        
        if (jobPhoneNormalized) {
          foundClient = clients.find(client => {
            const clientPhoneNormalized = normalizePhone(client.phone || '');
            const match = clientPhoneNormalized === jobPhoneNormalized || 
                         clientPhoneNormalized.endsWith(jobPhoneNormalized) ||
                         jobPhoneNormalized.endsWith(clientPhoneNormalized);
            if (match) {
              console.log('📞 Phone match found:', {
                clientPhone: client.phone,
                clientPhoneNormalized,
                jobPhone: job?.clientPhone,
                jobPhoneNormalized
              });
            }
            return match;
          });
        }
        
        // If not found by phone, try by name
        if (!foundClient && jobNameNormalized) {
          foundClient = clients.find(client => {
            const clientNameNormalized = (client.name || '').toLowerCase().trim();
            const match = clientNameNormalized === jobNameNormalized;
            if (match) {
              console.log('👤 Name match found:', {
                clientName: client.name,
                jobName: job?.clientName
              });
            }
            return match;
          });
        }
        
        if (foundClient) {
          clientId = foundClient._id;
          console.log('✅ Found client:', {
            clientId,
            clientName: foundClient.name,
            clientPhone: foundClient.phone,
            matchedBy: foundClient.phone && normalizePhone(foundClient.phone) === jobPhoneNormalized ? 'phone' : 'name'
          });
        } else {
          console.warn('⚠️ Client not found. Searched:', {
            byPhone: jobPhoneNormalized,
            byName: jobNameNormalized,
            availableClients: clients.length
          });
        }
      } catch (error) {
        console.error('⚠️ Error fetching clients to find client:', error);
      }
    }

    // If still no clientId, try to create client automatically if we have enough info
    if (!clientId && (job?.clientPhone || job?.clientName)) {
      try {
        console.log('📝 Creating client automatically from job data...');
        const newClientData = {
          name: job.clientName || t('common.unknownClient'),
          phone: job.clientPhone || '',
          email: job.clientEmail || '',
          address: job.clientAddress ? {
            street: job.clientAddress,
            number: '',
            isHouse: false
          } : undefined
        };
        
        const createdClient = await dispatch(createClientAsync(newClientData)).unwrap();
        clientId = createdClient._id;
        console.log('✅ Client created successfully:', { clientId, name: createdClient.name });
      } catch (error) {
        console.error('❌ Error creating client:', error);
        // Don't show error toast - just continue without clientId
        // The rating will still be saved in the report
      }
    }
    
    // If still no clientId, we'll still show the prompt but won't be able to save the rating to client
    // The rating will still be saved in the job report
    if (!clientId) {
      console.log('⚠️ promptClientRating: No clientId found. Rating will be saved in job report only.');
    }

    // Use the provided rating value, or fall back to reportData.customerRating
    const rating = ratingValue !== null ? ratingValue : reportData.customerRating;
    console.log('⭐ promptClientRating called with rating:', rating, 'from reportData:', reportData.customerRating, 'clientId:', clientId);
    
    if (rating && rating >= 1 && rating <= 10) {
      if (clientId) {
        try {
          console.log('💾 Saving client rating:', rating, 'for clientId:', clientId);
          await dispatch(updateClientAsync({
            id: clientId,
            updatedClientData: { rating: rating }
          })).unwrap();
          toast.success(`Ocena klijenta (${rating}/10) je sačuvana.`);
          return;
        } catch (error) {
          const message = error?.message || error?.payload || 'Greška pri čuvanju ocene klijenta.';
          console.error('❌ Error saving client rating:', error);
          toast.error(message);
          // Continue to show prompt if saving failed
        }
      } else {
        // Rating will be saved in the job report even if client doesn't exist
        console.log('⚠️ Cannot save rating to client: no clientId available. Rating will be saved in job report.');
        toast.info('Ocena će biti sačuvana u izveštaju posla.');
        return;
      }
    }

    // Fallback to prompt if rating not in reportData or if saving failed
    const clientLabel = job.clientName ? `"${job.clientName}"` : 'klijenta';

    while (true) {
      const input = window.prompt(`Ocenite ${clientLabel} (1-10):`, '10');

      if (input === null) {
        const skip = window.confirm('Ocena nije uneta. Želite li da preskočite ocenjivanje klijenta?');
        if (skip) {
          return;
        }
        continue;
      }

      const parsed = Number(input);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > 10) {
        toast.warn('Ocena mora biti broj između 1 i 10.');
        continue;
      }

      if (clientId) {
        try {
          await dispatch(updateClientAsync({
            id: clientId,
            updatedClientData: { rating: parsed }
          })).unwrap();
          toast.success('Ocena klijenta je sačuvana.');
        } catch (error) {
          const message = error?.message || error?.payload || 'Greška pri čuvanju ocene klijenta.';
          toast.error(message);
        }
      } else {
        // Rating will be saved in the job report even if client doesn't exist
        toast.info('Ocena će biti sačuvana u izveštaju posla.');
      }
      return;
    }
  }, [dispatch, job, reportData.customerRating, user?.token]);

  const updateJobStatus = async (newStatus, timerData = {}) => {
    try {
      setLoading(true);
      const token = user?.token;
      
      // Prepare update data
      const updateData = { status: newStatus, ...timerData };
      
      // Update job status and timer data
      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update worker status
      const workerDbStatus = mapJobStatusToWorkerStatus(newStatus);

      await axios.put(
        `${API_BASE_URL}/api/worker/${user.result._id}/status`,
        {
          status: workerDbStatus,
          currentJobId: job._id,
          location: job.clientAddress || 'Nepoznata adresa',
          notes: `Posao: ${job._id}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentStatus(newStatus.toLowerCase());
      
      // If status is being changed to Finished, prompt for client rating
      // (unless it's already been handled in submitReport)
      if (newStatus === 'Finished' && job.status !== 'Finished') {
        // Only prompt if we haven't already handled rating in submitReport
        // Check if reportData has a rating (which means submitReport was called)
        const hasRatingInReport = reportData?.customerRating && reportData.customerRating >= 1 && reportData.customerRating <= 10;
        if (!hasRatingInReport) {
          console.log('⭐ Status changed to Completed, prompting for client rating');
          await promptClientRating();
        }
      }
      
      // Emit WebSocket event instead of calling onStatusUpdate
      // Include timer data so admin can see when timers are started
      const travelStart =
        'travelStartTime' in timerData ? timerData.travelStartTime : job.travelStartTime;
      const workStart =
        'workStartTime' in timerData ? timerData.workStartTime : job.workStartTime;

      emitJobStatusUpdate({
        jobId: job._id,
        status: newStatus,
        workerId: user?.result?._id,
        workerName: user?.result?.name,
        timestamp: new Date().toISOString(),
        travelStartTime: travelStart instanceof Date ? travelStart.toISOString() : travelStart,
        workStartTime: workStart instanceof Date ? workStart.toISOString() : workStart,
        accumulatedTravelTime: timerData.accumulatedTravelTime ?? job.accumulatedTravelTime ?? 0,
        accumulatedWorkTime: timerData.accumulatedWorkTime ?? job.accumulatedWorkTime ?? 0
      });

      emitWorkerStatusUpdate({
        workerId: user?.result?._id,
        status: workerDbStatus,
        currentJobId: job._id,
        location: job.clientAddress || '',
        notes: `Posao: ${job.clientName || job._id}`
      });
      
      if (onStatusUpdate) {
        onStatusUpdate(newStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      console.error('Job ID:', job._id);
      console.error('New Status:', newStatus);
      console.error('User ID:', user?.result?._id);
      
      // Show user-friendly error message
      if (error.response?.status === 404) {
        console.error('Job not found or API endpoint incorrect');
      } else if (error.response?.status === 401) {
        console.error('Unauthorized - token may be expired');
      } else {
        console.error('Unknown error:', error.response?.data || error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const startTravel = async () => {
    console.log('🚀 startTravel CALLED', {
      jobId: job?._id,
      isTraveling,
      isWorking,
      hasActiveTravelOrWork,
      hasOtherActiveJob: !!hasOtherActiveJob,
      activeJobForWorker: activeJobForWorker?._id,
      timestamp: new Date().toISOString()
    });
    
    // 🔐 BLOKADA: Ne dozvoli pokretanje putovanja ako već postoji aktivno putovanje ili posao
    // DIREKTNA PROVERA: Proveri lokalno stanje, localStorage i Redux store
    // Vraća objekat sa informacijom o aktivnom poslu
    const checkActiveTravelOrWork = () => {
      // 1. Proveri lokalno stanje za trenutni posao
      if (isTraveling || isWorking) {
        console.log('🔐 BLOKADA [startTravel]: Lokalno stanje aktivno', { isTraveling, isWorking });
        return { hasActive: true, activeJob: job, isCurrentJob: true };
      }
      
      // 2. Proveri localStorage za sve poslove, ali SAMO ako posao stvarno ima aktivno stanje u Redux store-u
      if (typeof window !== 'undefined') {
        const allStorageKeys = Object.keys(localStorage);
        const travelKeys = allStorageKeys.filter(key => key.startsWith('isTraveling_') && key !== `isTraveling_${job?._id}`);
        const workKeys = allStorageKeys.filter(key => key.startsWith('isWorking_') && key !== `isWorking_${job?._id}`);
        
        // Proveri da li postoji aktivno stanje u localStorage
        const activeTravelKeys = travelKeys.filter(key => localStorage.getItem(key) === 'true');
        const activeWorkKeys = workKeys.filter(key => localStorage.getItem(key) === 'true');
        
        // Validacija: Proveri da li poslovi sa aktivnim stanjem u localStorage stvarno imaju aktivno stanje u Redux store-u
        let foundActiveJob = null;
        
        for (const key of activeTravelKeys) {
          const otherJobId = key.replace('isTraveling_', '');
          const otherJob = allJobs.find(j => j?._id === otherJobId);
          if (otherJob && (!!otherJob.travelStartTime || otherJob.status === 'On Road')) {
            foundActiveJob = otherJob;
            console.log('🔐 BLOKADA [startTravel]: Validno aktivno putovanje u localStorage i Redux', { jobId: otherJobId, job: otherJob });
            break;
          }
        }
        
        if (!foundActiveJob) {
          for (const key of activeWorkKeys) {
            const otherJobId = key.replace('isWorking_', '');
            const otherJob = allJobs.find(j => j?._id === otherJobId);
            if (otherJob && (!!otherJob.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(otherJob.status))) {
              foundActiveJob = otherJob;
              console.log('🔐 BLOKADA [startTravel]: Validno aktivni rad u localStorage i Redux', { jobId: otherJobId, job: otherJob });
              break;
            }
          }
        }
        
        // Ako nema validnog aktivnog stanja, očisti nevažeće localStorage zapise
        if (!foundActiveJob && activeTravelKeys.length > 0) {
          console.log('🧹 Čišćenje nevažećih localStorage zapisa za putovanje', { keys: activeTravelKeys });
          activeTravelKeys.forEach(key => localStorage.removeItem(key));
        }
        
        if (!foundActiveJob && activeWorkKeys.length > 0) {
          console.log('🧹 Čišćenje nevažećih localStorage zapisa za rad', { keys: activeWorkKeys });
          activeWorkKeys.forEach(key => localStorage.removeItem(key));
        }
        
        if (foundActiveJob) {
          console.log('🔐 BLOKADA [startTravel]: Aktivno stanje u localStorage i Redux', { activeJob: foundActiveJob });
          return { hasActive: true, activeJob: foundActiveJob, isCurrentJob: false };
        }
      }
      
      // 3. Proveri Redux store - koristi activeJobForWorker ako postoji
      if (hasActiveTravelOrWork) {
        if (activeJobForWorker && activeJobForWorker._id && activeJobForWorker._id.toString() !== job?._id?.toString()) {
          return { hasActive: true, activeJob: activeJobForWorker, isCurrentJob: false };
        } else if (currentJobHasActiveTravelOrWork) {
          return { hasActive: true, activeJob: job, isCurrentJob: true };
        }
        return { hasActive: true, activeJob: null, isCurrentJob: false };
      }
      
      return { hasActive: false, activeJob: null, isCurrentJob: false };
    };
    
    const activeCheckResult = checkActiveTravelOrWork();
    
    if (activeCheckResult.hasActive) {
      const foundActiveJob = activeCheckResult.activeJob;
      const isCurrentJob = activeCheckResult.isCurrentJob;
      
      // 🔍 DETALJNO LOGOVANJE ZA DEBUG
      console.log('🔍 [TOAST DEBUG - startTravel] Provera aktivnog stanja:', {
        'TRENUTNI POSAO (job)': {
          id: job?._id,
          clientName: job?.clientName,
          clientAddress: job?.clientAddress,
          status: job?.status,
          travelStartTime: job?.travelStartTime,
          workStartTime: job?.workStartTime,
          isTraveling: isTraveling,
          isWorking: isWorking
        },
        'PRONAĐENI AKTIVNI POSAO (foundActiveJob)': {
          id: foundActiveJob?._id,
          clientName: foundActiveJob?.clientName,
          clientAddress: foundActiveJob?.clientAddress,
          status: foundActiveJob?.status,
          travelStartTime: foundActiveJob?.travelStartTime,
          workStartTime: foundActiveJob?.workStartTime
        },
        'PROVERE': {
          isCurrentJob: isCurrentJob,
          foundActiveJobExists: !!foundActiveJob,
          foundActiveJobId: foundActiveJob?._id,
          currentJobId: job?._id,
          areIdsDifferent: foundActiveJob?._id?.toString() !== job?._id?.toString()
        }
      });
      
      // Ako je aktivni posao DRUGI posao (ne trenutni)
      if (!isCurrentJob && foundActiveJob) {
        // Odredi tip aktivne akcije u DRUGOM poslu
        const hasActiveTravel = !!foundActiveJob.travelStartTime || foundActiveJob.status === 'On Road';
        const hasActiveWork = !!foundActiveJob.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(foundActiveJob.status);
        const activeType = hasActiveTravel ? 'putovanje' : (hasActiveWork ? 'rad' : 'akciju');
        
        // Koristi podatke iz PRONAĐENOG aktivnog posla, ne trenutnog
        const activeClientName = foundActiveJob.clientName || 'Stranka';
        const clientAddress = foundActiveJob.clientAddress || '';
        
        // Formatiraj adresu - može biti string ili objekat
        let addressString = '';
        if (typeof clientAddress === 'object' && clientAddress !== null) {
          // Ako je objekat, sastavi adresu iz delova
          const parts = [];
          if (clientAddress.street) parts.push(clientAddress.street);
          if (clientAddress.number) parts.push(clientAddress.number);
          if (clientAddress.floor) parts.push(`spr. ${clientAddress.floor}`);
          if (clientAddress.apartment) parts.push(`st. ${clientAddress.apartment}`);
          addressString = parts.length > 0 ? parts.join(', ') : '';
        } else if (typeof clientAddress === 'string' && clientAddress.trim()) {
          addressString = clientAddress.trim();
        }
        
        // Dodaj informacije o vremenu ako postoje
        let timeInfo = '';
        if (hasActiveTravel && foundActiveJob.travelStartTime) {
          try {
            const travelStart = new Date(foundActiveJob.travelStartTime);
            const travelTimeMinutes = Math.floor((Date.now() - travelStart.getTime()) / 1000 / 60);
            timeInfo = travelTimeMinutes > 0 ? ` (pokrenuto pre ${travelTimeMinutes} minuta)` : ' (upravo pokrenuto)';
          } catch (e) {}
        } else if (hasActiveWork && foundActiveJob.workStartTime) {
          try {
            const workStart = new Date(foundActiveJob.workStartTime);
            const workTimeMinutes = Math.floor((Date.now() - workStart.getTime()) / 1000 / 60);
            timeInfo = workTimeMinutes > 0 ? ` (pokrenut pre ${workTimeMinutes} minuta)` : ' (upravo pokrenut)';
          } catch (e) {}
        } else if (foundActiveJob.status) {
          timeInfo = ` (status: ${foundActiveJob.status})`;
        }
        
        const addressPart = addressString ? ` na adresi "${addressString}"` : '';
        const toastMessage = `Već imate pokrenuto ${activeType}${timeInfo} za stranku "${activeClientName}"${addressPart}. Prvo završite ili zaustavite ${activeType} pre pokretanja novog putovanja.`;
        
        // 🔍 LOG TOAST PORUKE
        console.log('🔍 [TOAST DEBUG - startTravel] PRIKAZUJE SE PORUKA ZA DRUGI POSAO:', {
          activeType: activeType,
          timeInfo: timeInfo,
          activeClientName: activeClientName,
          addressString: addressString,
          addressPart: addressPart,
          fullToastMessage: toastMessage,
          'PODACI IZ PRONAĐENOG AKTIVNOG POSLA (foundActiveJob)': {
            id: foundActiveJob._id,
            clientName: foundActiveJob.clientName,
            clientAddress: foundActiveJob.clientAddress,
            status: foundActiveJob.status,
            travelStartTime: foundActiveJob.travelStartTime,
            workStartTime: foundActiveJob.workStartTime
          }
        });
        
        toast.warning(toastMessage);
        return;
      }
      
      // DRUGO: Ako nema aktivnog stanja u drugom poslu, proveri trenutni posao
      if (currentJobHasActiveTravelOrWork) {
        const hasActiveTravel = isTraveling || !!job?.travelStartTime || job?.status === 'On Road';
        const hasActiveWork = isWorking || !!job?.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(job?.status);
        
        // Detaljna poruka sa informacijama o aktivnom stanju
        const clientName = job?.clientName || 'Stranka';
        const clientAddress = job?.clientAddress || '';
        let addressString = '';
        if (typeof clientAddress === 'object' && clientAddress !== null) {
          const parts = [];
          if (clientAddress.street) parts.push(clientAddress.street);
          if (clientAddress.number) parts.push(clientAddress.number);
          if (clientAddress.floor) parts.push(`spr. ${clientAddress.floor}`);
          if (clientAddress.apartment) parts.push(`st. ${clientAddress.apartment}`);
          addressString = parts.length > 0 ? ` na adresi "${parts.join(', ')}"` : '';
        } else if (typeof clientAddress === 'string' && clientAddress.trim()) {
          addressString = ` na adresi "${clientAddress.trim()}"`;
        }
        
        let message = `Već imate aktivno `;
        const activeTypes = [];
        
        if (hasActiveTravel) {
          // Proveri sve izvore za travelStartTime
          const travelStartTimeValue = job?.travelStartTime || (isTraveling && travelStartTime) || null;
          let travelTimeText = '';
          if (travelStartTimeValue) {
            try {
              const travelStart = new Date(travelStartTimeValue);
              const travelTimeMinutes = Math.floor((Date.now() - travelStart.getTime()) / 1000 / 60);
              travelTimeText = travelTimeMinutes > 0 ? ` (pokrenuto pre ${travelTimeMinutes} minuta)` : ' (upravo pokrenuto)';
            } catch (e) {
              travelTimeText = '';
            }
          } else {
            travelTimeText = ' (status: ' + (job?.status || t('common.notAvailable')) + ')';
          }
          activeTypes.push(`putovanje${travelTimeText}`);
        }
        
        if (hasActiveWork) {
          // Proveri sve izvore za workStartTime
          const workStartTimeValue = job?.workStartTime || (isWorking && workStartTime) || null;
          let workTimeText = '';
          if (workStartTimeValue) {
            try {
              const workStart = new Date(workStartTimeValue);
              const workTimeMinutes = Math.floor((Date.now() - workStart.getTime()) / 1000 / 60);
              workTimeText = workTimeMinutes > 0 ? ` (pokrenut pre ${workTimeMinutes} minuta)` : ' (upravo pokrenut)';
            } catch (e) {
              workTimeText = '';
            }
          } else {
            workTimeText = ' (status: ' + (job?.status || t('common.notAvailable')) + ')';
          }
          activeTypes.push(`rad${workTimeText}`);
        }
        
        if (activeTypes.length > 0) {
          message += activeTypes.join(' i ') + ` za stranku "${clientName}"${addressString} u ovom poslu.`;
        } else {
          message += `putovanje ili rad za stranku "${clientName}"${addressString} u ovom poslu.`;
        }
        
        message += ' Prvo završite ili zaustavite aktivno stanje pre pokretanja novog putovanja.';
        
        // 🔍 LOG TOAST PORUKE ZA TRENUTNI POSAO
        console.log('🔍 [TOAST DEBUG - startTravel] PRIKAZUJE SE PORUKA ZA TRENUTNI POSAO:', {
          hasActiveTravel: hasActiveTravel,
          hasActiveWork: hasActiveWork,
          isTraveling: isTraveling,
          isWorking: isWorking,
          clientName: clientName,
          addressString: addressString,
          activeTypes: activeTypes,
          fullToastMessage: message,
          'PODACI IZ TRENUTNOG POSLA (job)': {
            id: job?._id,
            clientName: job?.clientName,
            clientAddress: job?.clientAddress,
            status: job?.status,
            travelStartTime: job?.travelStartTime,
            workStartTime: job?.workStartTime
          },
          'LOKALNO STANJE': {
            isTraveling: isTraveling,
            isWorking: isWorking,
            travelStartTime: travelStartTime,
            workStartTime: workStartTime
          }
        });
        
        toast.warning(message);
        return;
      }
    }
    const realNow = Date.now();
    const realNowISO = new Date().toISOString();
    
    // LOG: Start travel - realno vreme i tajmer vreme
    console.log('🚀 START TRAVEL - POKRETANJE:', {
      jobId: job?._id,
      clientName: job?.clientName,
      realNow: realNowISO,
      realNowTimestamp: realNow,
      accumulatedTravelTimeRef_BEFORE: accumulatedTravelTimeRef.current,
      accumulatedTravelTimeRef_BEFORE_minutes: Math.floor(accumulatedTravelTimeRef.current / 60),
      accumulatedWorkTimeRef_BEFORE: accumulatedWorkTimeRef.current,
      accumulatedWorkTimeRef_BEFORE_minutes: Math.floor(accumulatedWorkTimeRef.current / 60),
      job_accumulatedTravelTime: job?.accumulatedTravelTime,
      job_accumulatedWorkTime: job?.accumulatedWorkTime,
      useAIForThisJob: useAIForThisJob,
      timestamp: new Date().toISOString()
    });
    
    // LOG: Before starting travel
    console.log('🚀 START TRAVEL - BEFORE:', {
      jobId: job?._id,
      clientName: job?.clientName,
      realNow: realNowISO,
      realNowTimestamp: realNow,
      isWorking,
      workTime,
      accumulatedWorkTimeRef: accumulatedWorkTimeRef.current,
      accumulatedTravelTimeRef: accumulatedTravelTimeRef.current,
      travelStartTime_before: travelStartTime,
      timestamp: new Date().toISOString()
    });
    
    // Stop work if it's active
    if (isWorking) {
      setIsWorking(false);
      const currentSessionTime = workTime - accumulatedWorkTimeRef.current;
      accumulatedWorkTimeRef.current += currentSessionTime;
      
      console.log('🚀 START TRAVEL - STOPPING WORK:', {
        jobId: job?._id,
        workTime,
        accumulatedWorkTimeRef_before: accumulatedWorkTimeRef.current - currentSessionTime,
        currentSessionTime,
        accumulatedWorkTimeRef_after: accumulatedWorkTimeRef.current,
        timestamp: new Date().toISOString()
      });
      
      setWorkStartTime(null);
      saveAccumulatedTimes();
      // Clear work timer from localStorage
      const jobId = job?._id;
      if (jobId) {
        localStorage.removeItem(`isWorking_${jobId}`);
        localStorage.removeItem(`workStartTime_${jobId}`);
      }
      
      // Save accumulated work time to database
      try {
        const token = user?.token;
        await axios.patch(
          `${API_BASE_URL}/api/jobs/${job._id}`,
          {
            accumulatedWorkTime: accumulatedWorkTimeRef.current,
            workStartTime: null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('💾 SAVED WORK TIME TO DB:', {
          jobId: job?._id,
          accumulatedWorkTime: accumulatedWorkTimeRef.current,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving work time to database:', error);
      }
    }
    
    // Always show diagnosis when starting travel (if AI is enabled)
    // Removed isFirstStart check - allow diagnosis every time travel starts
    const jobId = job?._id;
    
    console.log('🤖 [AI DIAGNOSIS] startTravel - Check:', {
      jobId,
      clientName: job?.clientName,
      useAIForThisJob: useAIForThisJob,
      job_travelStartTime: job?.travelStartTime,
      job_status: job?.status,
      willShowAI: useAIForThisJob,
      timestamp: new Date().toISOString()
    });
    
    if (useAIForThisJob) {
      console.log('🤖 [AI DIAGNOSIS] ✅ CONDITIONS MET - About to show diagnosis modal');
      console.log('🤖 [AI DIAGNOSIS] Calling fetchAIDiagnosis() asynchronously (non-blocking)...');
      // Show diagnosis modal asynchronously - don't block worker departure
      // Worker can continue while diagnosis loads in background
      fetchAIDiagnosis().catch(error => {
        console.error('🤖 [AI DIAGNOSIS] Error in background diagnosis fetch:', error);
        // Don't block worker - just log error
      });
      console.log('🤖 [AI DIAGNOSIS] fetchAIDiagnosis() started (non-blocking)');
    } else {
      console.log('🤖 [AI DIAGNOSIS] ❌ SKIPPING - AI disabled for this job');
    }
    
    const now = new Date();
    const travelStartTimeValue = now.getTime();
    
    // LOG: Starting travel timer
    console.log('🚀 START TRAVEL - STARTING TIMER:', {
      jobId: job?._id,
      clientName: job?.clientName,
      realNow: realNowISO,
      realNowTimestamp: realNow,
      travelStartTime: travelStartTimeValue,
      travelStartTimeISO: now.toISOString(),
      accumulatedTravelTimeRef_before: accumulatedTravelTimeRef.current,
      accumulatedTravelTimeRef_beforeMinutes: Math.floor(accumulatedTravelTimeRef.current / 60),
      timestamp: new Date().toISOString()
    });
    
    setIsTraveling(true);
    setIsPaused(false);
    setTravelStartTime(travelStartTimeValue);
    setWorkStartTime(null);
    
    // Save timer state to localStorage (for backup)
    // jobId is already declared above
    if (jobId) {
      localStorage.setItem(`isTraveling_${jobId}`, 'true');
      localStorage.setItem(`travelStartTime_${jobId}`, travelStartTimeValue.toString());
    }
    
    // Ne ažuriraj status odmah, samo pokreni timer
    addTimeLog('start_travel', 'Krenuo ka stranci');
    
    // LOG: Before updating job status
    console.log('🚀 START TRAVEL - BEFORE UPDATE STATUS:', {
      jobId: job?._id,
      travelStartTime: travelStartTimeValue,
      travelStartTimeISO: now.toISOString(),
      accumulatedTravelTimeRef: accumulatedTravelTimeRef.current,
      accumulatedTravelTimeRefMinutes: Math.floor(accumulatedTravelTimeRef.current / 60),
      accumulatedWorkTimeRef: accumulatedWorkTimeRef.current,
      accumulatedWorkTimeRefMinutes: Math.floor(accumulatedWorkTimeRef.current / 60),
      timestamp: new Date().toISOString()
    });
    
    // Update job status to 'On Road' and save timer state to database
    await updateJobStatus('On Road', {
      travelStartTime: now,
      workStartTime: null,
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      accumulatedWorkTime: accumulatedWorkTimeRef.current
    });
    
    // LOG: After updating job status
    console.log('🚀 START TRAVEL - AFTER UPDATE STATUS:', {
      jobId: job?._id,
      travelStartTime: travelStartTimeValue,
      accumulatedTravelTimeRef: accumulatedTravelTimeRef.current,
      accumulatedWorkTimeRef: accumulatedWorkTimeRef.current,
      timestamp: new Date().toISOString()
    });
    
    // Update worker status to 'on_the_road' so admin can see it
    updateWorkerStatusOnly('on_the_road', `Putuje ka stranci: ${job.clientName || 'Stranka'}`);
  };

  // Helper function to extract error code from description
  const extractErrorCode = (description) => {
    if (!description) return null;
    
    // Remove quotes and normalize
    const normalized = description.trim().replace(/["'`]/g, '').toUpperCase();
    
    // Support multiple formats:
    // - E1, E15, F3, A10, H2, i10 (normalized to I10), E3A
    // - ER-01, ER01
    // - E11-2, E31-2-3 (hyphenated variants)
    const patterns = [
      // Hyphenated: ER-01, E31-2-3, E41-2-3-4
      /\b([A-Z]{1,3}-\d{1,3}(?:-\d{1,3}){0,3})\b/g,
      // Letter+digits then hyphen groups: E11-2, E61-3
      /\b([A-Z]{1,3}\d{1,3}(?:-\d{1,3}){1,3})\b/g,
      // Standard: E1, E15, A10, H2, E3A
      /\b([A-Z]{1,3}\d{1,3}[A-Z]?)\b/g,
      // Two-letter codes: CA, EE, EH, EI
      /\b([A-Z]{2})\b/g
    ];

    const allMatches = [];
    for (const re of patterns) {
      const m = normalized.match(re);
      if (m && m.length) {
        allMatches.push(...m);
      }
    }

    if (allMatches.length > 0) {
      // Prefer the longest/most specific match (e.g. ER-01 over ER, E31-2-3 over E31)
      const code = allMatches
        .map((s) => s.toUpperCase())
        .sort((a, b) => b.length - a.length)[0];
      console.log('🔍 [EXTRACT] Extracted error code:', code, 'from:', description);
      return code;
    }
    
    // Also try to match if description is just the error code (e.g., "C3" or '"C3"')
    const directMatch = normalized.match(/^([A-Z]{1,3}-\d{1,3}(?:-\d{1,3}){0,3}|[A-Z]{1,3}\d{1,3}(?:-\d{1,3}){1,3}|[A-Z]{1,3}\d{1,3}[A-Z]?|[A-Z]{2})$/);
    if (directMatch) {
      const code = directMatch[1].toUpperCase();
      console.log('🔍 [EXTRACT] Direct match error code:', code, 'from:', description);
      return code;
    }
    
    console.log('🔍 [EXTRACT] No error code found in:', description);
    return null;
  };

  // Helper function to search local solutions
  const searchLocalSolutions = async (deviceType, deviceBrand, issueDescription) => {
    try {
      console.log('🔍 [LOCAL SEARCH] Starting search:', { deviceType, deviceBrand, issueDescription });
      
      // Normalize device type for file name (handle various formats)
      const normalizeDeviceType = (type) => {
        if (!type) return null;
        const normalized = type.toLowerCase().trim();
        
        // Map various formats to file names
        const deviceTypeMap = {
          // Veš mašina variations (handle with/without diacritics, spaces, capitalization)
          'veš mašina': 'ves-masina',
          'ves masina': 'ves-masina',
          'ves masine': 'ves-masina', // Plural form (your exact format)
          'veš masine': 'ves-masina', // Plural form with diacritics
          'ves mašina': 'ves-masina',
          'veš masina': 'ves-masina',
          'vesmasina': 'ves-masina',
          'vesmašina': 'ves-masina',
          // Suđe mašina variations
          'suđe mašina': 'sudje-masina',
          'sudje masina': 'sudje-masina',
          'suđe masina': 'sudje-masina',
          'sudje mašina': 'sudje-masina',
          // Mašina za sušenje
          'mašina za sušenje': 'masina-za-susenje',
          'masina za susenje': 'masina-za-susenje',
          // Frižider
          'frižider': 'frizideri',
          'frizider': 'frizideri',
          // Aspirator
          'aspirator': 'aspiratori',
          // Bojler
          'bojler': 'bojleri',
          // Šporet
          'šporet': 'sporeti',
          'sporet': 'sporeti',
          // Pegla
          'pegla': 'pegle',
          // Kafe aparat
          'kafe aparat': 'kafe-aparati',
          'kafeaparat': 'kafe-aparati',
          // Mikrotalasna
          'mikrotalasna': 'mikrotalasne',
          'mikrotalasna pećnica': 'mikrotalasne'
        };
        
        return deviceTypeMap[normalized] || null;
      };
      
      const folderName = normalizeDeviceType(deviceType);
      if (!folderName) {
        console.log('🔍 [LOCAL SEARCH] No local solutions folder for device type:', deviceType, 'Normalized:', deviceType?.toLowerCase()?.trim());
        return null;
      }
      
      console.log('🔍 [LOCAL SEARCH] Device type mapped:', deviceType, '→', folderName);

      // Učitaj common solutions jednom (koristi se i za šifru i za pretragu po opisu)
      const commonSolutionsPath = `/diagnosis-solutions/${folderName}/common-solutions.json`;
      const commonResponse = await fetch(commonSolutionsPath);
      if (!commonResponse.ok) {
        console.warn('⚠️ [LOCAL SEARCH] Common solutions file not found:', commonSolutionsPath);
        return null;
      }
      const commonSolutions = await commonResponse.json();

      const formatSolutionResponse = (solution, sourceErrorCode) => ({
        toolsAndParts: solution.toolsAndParts || [],
        possibleCauses: solution.possibleCauses || [],
        practicalRecommendations: solution.practicalRecommendations || [],
        generalTips: solution.generalTips || '',
        estimatedComplexity: solution.estimatedComplexity || 'srednje',
        source: 'local_database',
        errorCode: sourceErrorCode || undefined
      });

      // 1) Pokušaj pronaći rešenje po šifri greške (uključujući E↔F: E5 kao F5)
      const errorCode = extractErrorCode(issueDescription);
      console.log('🔍 [LOCAL SEARCH] Extracted error code:', errorCode, 'from:', issueDescription);

      if (errorCode && deviceBrand) {
        const normalizedBrand = deviceBrand.toLowerCase().trim();
        const baseBrand = normalizedBrand.split(/\s+/)[0];
        const brandFilePath = `/diagnosis-solutions/${folderName}/${baseBrand}.json`;
        const brandResponse = await fetch(brandFilePath);
        if (brandResponse.ok) {
          const brandData = await brandResponse.json();
          if (brandData?.errorCodeMappings) {
            const buildCandidates = (code) => {
              const candidates = [];
              if (!code) return candidates;
              const upper = code.toUpperCase();
              candidates.push(upper);
              if (upper.includes('-')) candidates.push(upper.replace(/-/g, ''));
              const parts = upper.split('-');
              if (parts.length > 1) {
                for (let i = parts.length - 1; i >= 1; i--) {
                  candidates.push(parts.slice(0, i).join('-'));
                }
                candidates.push(parts[0]);
              }
              const similarLetterMatch = upper.match(/^([EF])(\d.*)$/);
              if (similarLetterMatch) {
                const otherLetter = similarLetterMatch[1] === 'E' ? 'F' : 'E';
                candidates.push(otherLetter + similarLetterMatch[2]);
              }
              return [...new Set(candidates)];
            };
            const candidates = buildCandidates(errorCode);
            for (const c of candidates) {
              const solutionKey = brandData.errorCodeMappings[c];
              if (solutionKey) {
                const solution = commonSolutions[solutionKey];
                if (solution) {
                  console.log('✅ [LOCAL SEARCH] Found by error code:', { errorCode, matchedAs: c, solutionKey });
                  return formatSolutionResponse(solution, errorCode);
                }
              }
            }
          }
        }
      }

      // 2) Ako nema šifre ili šifra nije u bazi – pretraga po opisu (npr. "ne okrece motor")
      const normalizedDesc = (issueDescription || '').toLowerCase().trim();
      if (normalizedDesc.length >= 2) {
        let bestKey = null;
        let bestScore = 0;
        for (const [key, sol] of Object.entries(commonSolutions)) {
          if (!sol || !Array.isArray(sol.keywords)) continue;
          let score = 0;
          for (const kw of sol.keywords) {
            if (typeof kw === 'string' && normalizedDesc.includes(kw.toLowerCase())) score++;
          }
          if (score > bestScore) {
            bestScore = score;
            bestKey = key;
          }
        }
        if (bestKey && bestScore >= 1) {
          const solution = commonSolutions[bestKey];
          console.log('✅ [LOCAL SEARCH] Found by description keywords:', { bestKey, bestScore, sample: normalizedDesc.slice(0, 50) });
          return formatSolutionResponse(solution, null);
        }
      }

      console.log('🔍 [LOCAL SEARCH] No local solution (no code match, no keyword match)');
      return null;
    } catch (error) {
      console.error('❌ [LOCAL SEARCH] Error loading local solutions:', error);
      console.error('Error details:', error.message, error.stack);
      return null;
    }
  };

  const fetchAIDiagnosis = async () => {
    console.log('🤖 [AI DIAGNOSIS] fetchAIDiagnosis() called');
    console.log('🤖 [AI DIAGNOSIS] Setting showAIDiagnosisModal to true...');
    // Always show modal first
    setShowAIDiagnosisModal(true);
    console.log('🤖 [AI DIAGNOSIS] setShowAIDiagnosisModal(true) called');
    setAiDiagnosisLoading(true);
    setAiDiagnosisData(null);
    console.log('🤖 [AI DIAGNOSIS] Modal state initialized, starting diagnosis...');
    
    // Check what info we have
    const issueDescription = job?.issueDescription?.trim() || '';
    const deviceBrand = job?.deviceBrand?.trim() || '';
    const deviceModel = job?.deviceModel?.trim() || '';
    const deviceType = job?.deviceType?.trim() || '';
    
    const hasIssueDescription = formatDisplayValue(issueDescription) !== '-';
    
    const hasDeviceInfo = (deviceBrand && deviceBrand !== t('common.unknownBrand') && deviceBrand !== 'Unknown Brand') ||
                          (deviceModel && deviceModel !== t('common.unknownModel') && deviceModel !== 'Unknown Model') ||
                          (deviceType && deviceType !== t('common.unknownDevice') && deviceType !== 'Unknown Device');
    
    console.log('🔍 Diagnosis Check:', {
      hasIssueDescription,
      hasDeviceInfo,
      issueDescription,
      deviceBrand,
      deviceModel,
      deviceType,
      fullJob: job
    });
    
    // If we don't have enough info, show message in modal
    if (!hasIssueDescription && !hasDeviceInfo) {
      console.log('⚠️ Insufficient info for diagnosis');
      setAiDiagnosisData({
        error: false,
        insufficientInfo: true,
        message: 'Za preporuke potrebno je da posao ima opis problema ili informacije o uređaju (marka, model, tip)'
      });
      setAiDiagnosisLoading(false);
      return;
    }
    
    try {
      // STEP 1: First try to find local solution (FAST - < 100ms)
      console.log('🔍 [DIAGNOSIS] Step 1: Searching local solutions...', {
        deviceType,
        deviceBrand,
        issueDescription
      });
      
      const localSolution = await searchLocalSolutions(deviceType, deviceBrand, issueDescription);
      
      if (localSolution) {
        console.log('✅ [DIAGNOSIS] Found local solution - returning immediately', localSolution);
        setAiDiagnosisData(localSolution);
        setAiDiagnosisLoading(false);
        return;
      }

      console.warn('⚠️ [DIAGNOSIS] No local solution found - will call AI');
      
      // STEP 2: If no local solution, fallback to AI (SLOW - 15+ seconds)
      console.log('🔍 [DIAGNOSIS] Step 2: No local solution found, calling AI...');
      const deviceData = {
        deviceBrand: deviceBrand || '',
        deviceModel: deviceModel || '',
        deviceType: deviceType || '',
        issueDescription: hasIssueDescription ? issueDescription : 'Problem sa uređajem - nema detaljnog opisa'
      };
      
      console.log('📤 Sending AI diagnosis request:', deviceData);
      
      // NO TIMEOUT - let AI take as long as it needs
      const response = await analyzeDeviceIssue(deviceData);
      
      console.log('📥 AI diagnosis response:', response);
      
      if (response.data && response.data.success && response.data.data) {
        const aiData = {
          ...response.data.data,
          source: 'ai'
        };
        setAiDiagnosisData(aiData);
        console.log('✅ AI diagnosis data set successfully');
      } else {
        console.warn('⚠️ AI diagnosis response missing data:', response);
        setAiDiagnosisData({
          error: true,
          message: 'AI nije mogao da generiše preporuke. Pokušajte ponovo.'
        });
      }
    } catch (error) {
      console.error('❌ Error fetching diagnosis:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || error.message || 'Greška pri dobijanju preporuka';
      
      // Set error state in modal (don't close it)
      setAiDiagnosisData({
        error: true,
        message: errorMessage
      });
      
      // Show toast notification
      if (error.response?.status === 429) {
        toast.error('Prekoračen limit za AI. Molimo pokušajte kasnije.');
      } else if (error.response?.status === 500 && errorMessage.includes('API key')) {
        toast.error('OpenAI API key nije konfigurisan');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setAiDiagnosisLoading(false);
    }
  };

  const pauseTravel = useCallback(async () => {
    setIsTraveling(false);
    setIsPaused(true);
    // Sačuvaj trenutno vreme putovanja u ref (oduzmi akumulirano da dobijemo samo trenutno)
    const currentSessionTime = travelTime - accumulatedTravelTimeRef.current;
    accumulatedTravelTimeRef.current += currentSessionTime;
    setTravelStartTime(null);
    saveAccumulatedTimes(); // Sačuvaj u localStorage
    
    // Clear travel timer from localStorage
    const jobId = job?._id;
    if (jobId) {
      localStorage.removeItem(`isTraveling_${jobId}`);
      localStorage.removeItem(`travelStartTime_${jobId}`);
    }
    
    // Send WebSocket update immediately to notify admin that travel is paused
    // Do this BEFORE saving to database to ensure admin sees the update immediately
    console.log('⏸️ Worker: Pausing travel - sending WebSocket update', {
      jobId: job._id,
      workerId: user?.result?._id,
      workerName: user?.result?.name,
      travelStartTime: null, // Timer is paused
      workStartTime: workStartTime ? new Date(workStartTime).toISOString() : (job.workStartTime ? new Date(job.workStartTime).toISOString() : null),
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      accumulatedWorkTime: accumulatedWorkTimeRef.current,
      timestamp: new Date().toISOString()
    });
    
    emitJobStatusUpdate({
      jobId: job._id,
      status: job.status,
      workerId: user?.result?._id,
      workerName: user?.result?.name,
      timestamp: new Date().toISOString(),
      travelStartTime: null, // Timer is paused - explicitly set to null
      workStartTime: workStartTime ? new Date(workStartTime).toISOString() : (job.workStartTime ? new Date(job.workStartTime).toISOString() : null),
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      accumulatedWorkTime: accumulatedWorkTimeRef.current
    });
    
    // Save accumulated travel time to database
    try {
      const token = user?.token;
      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        {
          accumulatedTravelTime: accumulatedTravelTimeRef.current,
          travelStartTime: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving travel time to database:', error);
      // Even if database save fails, WebSocket update was already sent
    }
    
    addTimeLog('pause_travel', 'Pauzirao putovanje');
    
    // Update worker status - keep 'on_the_road' but paused
    updateWorkerStatusOnly('on_the_road', `Pauzirao putovanje - Posao: ${job.clientName || 'Stranka'}`);
  }, [travelTime, saveAccumulatedTimes, job, updateWorkerStatusOnly, user, emitJobStatusUpdate, workStartTime]);

  const startWork = async () => {
    console.log('🅿️ [PARKING MODAL] startWork - Full Check:', {
      jobId: job?._id,
      clientName: job?.clientName,
      enableParkingReminderForThisJob: enableParkingReminderForThisJob,
      showParkingModalState: showParkingModal,
      willShowModal: enableParkingReminderForThisJob === true,
      timestamp: new Date().toISOString()
    });
    
    // Check if parking reminder is enabled
    if (enableParkingReminderForThisJob) {
      console.log('🅿️ [PARKING MODAL] ✅ CONDITIONS MET - About to show parking modal');
      console.log('🅿️ [PARKING MODAL] Setting showParkingModal to true...');
      // Show parking modal before starting work
      setShowParkingModal(true);
      console.log('🅿️ [PARKING MODAL] showParkingModal set to true, returning early');
      return; // Don't start work yet, wait for user to confirm parking
    }
    
    console.log('🅿️ [PARKING MODAL] ❌ SKIPPING - Parking reminder disabled, proceeding directly to startWork');
    // If parking reminder is disabled, proceed directly
    await proceedWithStartWork();
  };

  const proceedWithStartWork = async () => {
    console.log('🚀 proceedWithStartWork CALLED', {
      jobId: job?._id,
      isTraveling,
      isWorking,
      hasActiveTravelOrWork,
      hasOtherActiveJob: !!hasOtherActiveJob,
      activeJobForWorker: activeJobForWorker?._id,
      timestamp: new Date().toISOString()
    });
    
    // 🔐 BLOKADA: Ne dozvoli pokretanje posla ako već postoji aktivno putovanje ili posao
    // DIREKTNA PROVERA: Proveri lokalno stanje, localStorage i Redux store
    // Vraća objekat sa informacijom o aktivnom poslu
    const checkActiveTravelOrWork = () => {
      // 1. Proveri lokalno stanje za trenutni posao
      if (isTraveling || isWorking) {
        console.log('🔐 BLOKADA [startWork]: Lokalno stanje aktivno', { isTraveling, isWorking });
        return { hasActive: true, activeJob: job, isCurrentJob: true };
      }
      
      // 2. Proveri localStorage za sve poslove, ali SAMO ako posao stvarno ima aktivno stanje u Redux store-u
      if (typeof window !== 'undefined') {
        const allStorageKeys = Object.keys(localStorage);
        const travelKeys = allStorageKeys.filter(key => key.startsWith('isTraveling_') && key !== `isTraveling_${job?._id}`);
        const workKeys = allStorageKeys.filter(key => key.startsWith('isWorking_') && key !== `isWorking_${job?._id}`);
        
        // Proveri da li postoji aktivno stanje u localStorage
        const activeTravelKeys = travelKeys.filter(key => localStorage.getItem(key) === 'true');
        const activeWorkKeys = workKeys.filter(key => localStorage.getItem(key) === 'true');
        
        // Validacija: Proveri da li poslovi sa aktivnim stanjem u localStorage stvarno imaju aktivno stanje u Redux store-u
        let foundActiveJob = null;
        
        for (const key of activeTravelKeys) {
          const otherJobId = key.replace('isTraveling_', '');
          const otherJob = allJobs.find(j => j?._id === otherJobId);
          if (otherJob && (!!otherJob.travelStartTime || otherJob.status === 'On Road')) {
            foundActiveJob = otherJob;
            console.log('🔐 BLOKADA [startWork]: Validno aktivno putovanje u localStorage i Redux', { jobId: otherJobId, job: otherJob });
            break;
          }
        }
        
        if (!foundActiveJob) {
          for (const key of activeWorkKeys) {
            const otherJobId = key.replace('isWorking_', '');
            const otherJob = allJobs.find(j => j?._id === otherJobId);
            if (otherJob && (!!otherJob.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(otherJob.status))) {
              foundActiveJob = otherJob;
              console.log('🔐 BLOKADA [startWork]: Validno aktivni rad u localStorage i Redux', { jobId: otherJobId, job: otherJob });
              break;
            }
          }
        }
        
        // Ako nema validnog aktivnog stanja, očisti nevažeće localStorage zapise
        if (!foundActiveJob && activeTravelKeys.length > 0) {
          console.log('🧹 Čišćenje nevažećih localStorage zapisa za putovanje', { keys: activeTravelKeys });
          activeTravelKeys.forEach(key => localStorage.removeItem(key));
        }
        
        if (!foundActiveJob && activeWorkKeys.length > 0) {
          console.log('🧹 Čišćenje nevažećih localStorage zapisa za rad', { keys: activeWorkKeys });
          activeWorkKeys.forEach(key => localStorage.removeItem(key));
        }
        
        if (foundActiveJob) {
          console.log('🔐 BLOKADA [startWork]: Aktivno stanje u localStorage i Redux', { activeJob: foundActiveJob });
          return { hasActive: true, activeJob: foundActiveJob, isCurrentJob: false };
        }
      }
      
      // 3. Proveri Redux store - koristi activeJobForWorker ako postoji
      if (hasActiveTravelOrWork) {
        if (activeJobForWorker && activeJobForWorker._id && activeJobForWorker._id.toString() !== job?._id?.toString()) {
          return { hasActive: true, activeJob: activeJobForWorker, isCurrentJob: false };
        } else if (currentJobHasActiveTravelOrWork) {
          return { hasActive: true, activeJob: job, isCurrentJob: true };
        }
        return { hasActive: true, activeJob: null, isCurrentJob: false };
      }
      
      return { hasActive: false, activeJob: null, isCurrentJob: false };
    };
    
    const activeCheckResult = checkActiveTravelOrWork();
    
    if (activeCheckResult.hasActive) {
      const foundActiveJob = activeCheckResult.activeJob;
      const isCurrentJob = activeCheckResult.isCurrentJob;
      
      // 🔍 DETALJNO LOGOVANJE ZA DEBUG
      console.log('🔍 [TOAST DEBUG - proceedWithStartWork] Provera aktivnog stanja:', {
        'TRENUTNI POSAO (job)': {
          id: job?._id,
          clientName: job?.clientName,
          clientAddress: job?.clientAddress,
          status: job?.status,
          travelStartTime: job?.travelStartTime,
          workStartTime: job?.workStartTime,
          isTraveling: isTraveling,
          isWorking: isWorking
        },
        'PRONAĐENI AKTIVNI POSAO (foundActiveJob)': {
          id: foundActiveJob?._id,
          clientName: foundActiveJob?.clientName,
          clientAddress: foundActiveJob?.clientAddress,
          status: foundActiveJob?.status,
          travelStartTime: foundActiveJob?.travelStartTime,
          workStartTime: foundActiveJob?.workStartTime
        },
        'PROVERE': {
          isCurrentJob: isCurrentJob,
          foundActiveJobExists: !!foundActiveJob,
          foundActiveJobId: foundActiveJob?._id,
          currentJobId: job?._id,
          areIdsDifferent: foundActiveJob?._id?.toString() !== job?._id?.toString()
        }
      });
      
      // Ako je aktivni posao DRUGI posao (ne trenutni)
      if (!isCurrentJob && foundActiveJob) {
        // Odredi tip aktivne akcije u DRUGOM poslu
        const hasActiveTravel = !!foundActiveJob.travelStartTime || foundActiveJob.status === 'On Road';
        const hasActiveWork = !!foundActiveJob.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(foundActiveJob.status);
        const activeType = hasActiveTravel ? 'putovanje' : (hasActiveWork ? 'rad' : 'akciju');
        
        // Koristi podatke iz PRONAĐENOG aktivnog posla, ne trenutnog
        const activeClientName = foundActiveJob.clientName || 'Stranka';
        const clientAddress = foundActiveJob.clientAddress || '';
        
        // Formatiraj adresu - može biti string ili objekat
        let addressString = '';
        if (typeof clientAddress === 'object' && clientAddress !== null) {
          // Ako je objekat, sastavi adresu iz delova
          const parts = [];
          if (clientAddress.street) parts.push(clientAddress.street);
          if (clientAddress.number) parts.push(clientAddress.number);
          if (clientAddress.floor) parts.push(`spr. ${clientAddress.floor}`);
          if (clientAddress.apartment) parts.push(`st. ${clientAddress.apartment}`);
          addressString = parts.length > 0 ? parts.join(', ') : '';
        } else if (typeof clientAddress === 'string' && clientAddress.trim()) {
          addressString = clientAddress.trim();
        }
        
        // Dodaj informacije o vremenu ako postoje
        let timeInfo = '';
        if (hasActiveTravel && foundActiveJob.travelStartTime) {
          try {
            const travelStart = new Date(foundActiveJob.travelStartTime);
            const travelTimeMinutes = Math.floor((Date.now() - travelStart.getTime()) / 1000 / 60);
            timeInfo = travelTimeMinutes > 0 ? ` (pokrenuto pre ${travelTimeMinutes} minuta)` : ' (upravo pokrenuto)';
          } catch (e) {}
        } else if (hasActiveWork && foundActiveJob.workStartTime) {
          try {
            const workStart = new Date(foundActiveJob.workStartTime);
            const workTimeMinutes = Math.floor((Date.now() - workStart.getTime()) / 1000 / 60);
            timeInfo = workTimeMinutes > 0 ? ` (pokrenut pre ${workTimeMinutes} minuta)` : ' (upravo pokrenut)';
          } catch (e) {}
        } else if (foundActiveJob.status) {
          timeInfo = ` (status: ${foundActiveJob.status})`;
        }
        
        const addressPart = addressString ? ` na adresi "${addressString}"` : '';
        const toastMessage = `Već imate pokrenuto ${activeType}${timeInfo} za stranku "${activeClientName}"${addressPart}. Prvo završite ili zaustavite ${activeType} pre pokretanja novog rada.`;
        
        // 🔍 LOG TOAST PORUKE
        console.log('🔍 [TOAST DEBUG - proceedWithStartWork] PRIKAZUJE SE PORUKA ZA DRUGI POSAO:', {
          activeType: activeType,
          timeInfo: timeInfo,
          activeClientName: activeClientName,
          addressString: addressString,
          addressPart: addressPart,
          fullToastMessage: toastMessage,
          'PODACI IZ PRONAĐENOG AKTIVNOG POSLA (foundActiveJob)': {
            id: foundActiveJob._id,
            clientName: foundActiveJob.clientName,
            clientAddress: foundActiveJob.clientAddress,
            status: foundActiveJob.status,
            travelStartTime: foundActiveJob.travelStartTime,
            workStartTime: foundActiveJob.workStartTime
          }
        });
        
        toast.warning(toastMessage);
        return;
      }
      
      // DRUGO: Ako nema aktivnog stanja u drugom poslu, proveri trenutni posao
      if (currentJobHasActiveTravelOrWork) {
        const hasActiveTravel = isTraveling || !!job?.travelStartTime || job?.status === 'On Road';
        const hasActiveWork = isWorking || !!job?.workStartTime || ['In Repair', 'In Progress', 'At Client', 'Diagnosing'].includes(job?.status);
        
        // Detaljna poruka sa informacijama o aktivnom stanju
        const clientName = job?.clientName || 'Stranka';
        const clientAddress = job?.clientAddress || '';
        let addressString = '';
        if (typeof clientAddress === 'object' && clientAddress !== null) {
          const parts = [];
          if (clientAddress.street) parts.push(clientAddress.street);
          if (clientAddress.number) parts.push(clientAddress.number);
          if (clientAddress.floor) parts.push(`spr. ${clientAddress.floor}`);
          if (clientAddress.apartment) parts.push(`st. ${clientAddress.apartment}`);
          addressString = parts.length > 0 ? ` na adresi "${parts.join(', ')}"` : '';
        } else if (typeof clientAddress === 'string' && clientAddress.trim()) {
          addressString = ` na adresi "${clientAddress.trim()}"`;
        }
        
        let message = `Već imate aktivno `;
        const activeTypes = [];
        
        if (hasActiveTravel) {
          // Proveri sve izvore za travelStartTime
          const travelStartTimeValue = job?.travelStartTime || (isTraveling && travelStartTime) || null;
          let travelTimeText = '';
          if (travelStartTimeValue) {
            try {
              const travelStart = new Date(travelStartTimeValue);
              const travelTimeMinutes = Math.floor((Date.now() - travelStart.getTime()) / 1000 / 60);
              travelTimeText = travelTimeMinutes > 0 ? ` (pokrenuto pre ${travelTimeMinutes} minuta)` : ' (upravo pokrenuto)';
            } catch (e) {
              travelTimeText = '';
            }
          } else {
            travelTimeText = ' (status: ' + (job?.status || t('common.notAvailable')) + ')';
          }
          activeTypes.push(`putovanje${travelTimeText}`);
        }
        
        if (hasActiveWork) {
          // Proveri sve izvore za workStartTime
          const workStartTimeValue = job?.workStartTime || (isWorking && workStartTime) || null;
          let workTimeText = '';
          if (workStartTimeValue) {
            try {
              const workStart = new Date(workStartTimeValue);
              const workTimeMinutes = Math.floor((Date.now() - workStart.getTime()) / 1000 / 60);
              workTimeText = workTimeMinutes > 0 ? ` (pokrenut pre ${workTimeMinutes} minuta)` : ' (upravo pokrenut)';
            } catch (e) {
              workTimeText = '';
            }
          } else {
            workTimeText = ' (status: ' + (job?.status || t('common.notAvailable')) + ')';
          }
          activeTypes.push(`rad${workTimeText}`);
        }
        
        if (activeTypes.length > 0) {
          message += activeTypes.join(' i ') + ` za stranku "${clientName}"${addressString} u ovom poslu.`;
        } else {
          message += `putovanje ili rad za stranku "${clientName}"${addressString} u ovom poslu.`;
        }
        
        message += ' Prvo završite ili zaustavite aktivno stanje pre pokretanja novog rada.';
        
        // 🔍 LOG TOAST PORUKE ZA TRENUTNI POSAO
        console.log('🔍 [TOAST DEBUG - proceedWithStartWork] PRIKAZUJE SE PORUKA ZA TRENUTNI POSAO:', {
          hasActiveTravel: hasActiveTravel,
          hasActiveWork: hasActiveWork,
          isTraveling: isTraveling,
          isWorking: isWorking,
          clientName: clientName,
          addressString: addressString,
          activeTypes: activeTypes,
          fullToastMessage: message,
          'PODACI IZ TRENUTNOG POSLA (job)': {
            id: job?._id,
            clientName: job?.clientName,
            clientAddress: job?.clientAddress,
            status: job?.status,
            travelStartTime: job?.travelStartTime,
            workStartTime: job?.workStartTime
          },
          'LOKALNO STANJE': {
            isTraveling: isTraveling,
            isWorking: isWorking,
            travelStartTime: travelStartTime,
            workStartTime: workStartTime
          }
        });
        
        toast.warning(message);
        return;
      }
    }
    const realNow = Date.now();
    const realNowISO = new Date().toISOString();
    
    // Stop travel if it's active
    if (isTraveling) {
      const travelEndTime = Date.now();
      const travelStartTimeValue = travelStartTime;
      const travelElapsed = travelStartTimeValue ? Math.floor((travelEndTime - travelStartTimeValue) / 1000) : 0;
      
      // LOG: Stop travel timer
      console.log('🛑 STOP TRAVEL TIMER:', {
        jobId: job?._id,
        clientName: job?.clientName,
        realNow: realNowISO,
        realNowTimestamp: realNow,
        travelStartTime: travelStartTimeValue,
        travelStartTimeISO: travelStartTimeValue ? new Date(travelStartTimeValue).toISOString() : null,
        travelEndTime: travelEndTime,
        travelEndTimeISO: new Date(travelEndTime).toISOString(),
        travelElapsed,
        accumulatedTravelTimeBefore: accumulatedTravelTimeRef.current,
        travelTimeBefore: travelTime,
        currentSessionTime: travelTime - accumulatedTravelTimeRef.current,
        timestamp: new Date().toISOString()
      });
      
      setIsTraveling(false);
      const currentSessionTime = travelTime - accumulatedTravelTimeRef.current;
      accumulatedTravelTimeRef.current += currentSessionTime;
      
      // LOG: After calculation
      console.log('🛑 STOP TRAVEL TIMER (AFTER CALC):', {
        jobId: job?._id,
        accumulatedTravelTimeAfter: accumulatedTravelTimeRef.current,
        currentSessionTimeAdded: currentSessionTime,
        timestamp: new Date().toISOString()
      });
      
      setTravelStartTime(null);
      saveAccumulatedTimes();
      // Clear travel timer from localStorage
      const jobId = job?._id;
      if (jobId) {
        localStorage.removeItem(`isTraveling_${jobId}`);
        localStorage.removeItem(`travelStartTime_${jobId}`);
      }
      
      // Save accumulated travel time to database
      try {
        const token = user?.token;
        await axios.patch(
          `${API_BASE_URL}/api/jobs/${job._id}`,
          {
            accumulatedTravelTime: accumulatedTravelTimeRef.current,
            travelStartTime: null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Send WebSocket update immediately to notify admin that travel is stopped
        console.log('🛑 Worker: Stopping travel - sending WebSocket update', {
          jobId: job._id,
          workerId: user?.result?._id,
          workerName: user?.result?.name,
          travelStartTime: null, // Travel is stopped - explicitly set to null
          workStartTime: null, // Work hasn't started yet
          accumulatedTravelTime: accumulatedTravelTimeRef.current,
          accumulatedWorkTime: accumulatedWorkTimeRef.current,
          timestamp: new Date().toISOString()
        });
        
        emitJobStatusUpdate({
          jobId: job._id,
          status: job.status,
          workerId: user?.result?._id,
          workerName: user?.result?.name,
          timestamp: new Date().toISOString(),
          travelStartTime: null, // Travel is stopped - explicitly set to null
          workStartTime: null, // Work hasn't started yet
          accumulatedTravelTime: accumulatedTravelTimeRef.current,
          accumulatedWorkTime: accumulatedWorkTimeRef.current
        });
        
        // LOG: After save to DB
        console.log('💾 SAVED TRAVEL TIME TO DB:', {
          jobId: job?._id,
          accumulatedTravelTime: accumulatedTravelTimeRef.current,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving travel time to database:', error);
      }
    }
    
    const now = new Date();
    const workStartTimeValue = now.getTime();
    
    // LOG: Start work timer
    console.log('🚀 START WORK TIMER:', {
      jobId: job?._id,
      clientName: job?.clientName,
      realNow: realNowISO,
      realNowTimestamp: realNow,
      workStartTime: workStartTimeValue,
      workStartTimeISO: now.toISOString(),
      accumulatedWorkTimeBefore: accumulatedWorkTimeRef.current,
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      timestamp: new Date().toISOString()
    });
    
    setIsWorking(true);
    setIsPaused(false);
    setWorkStartTime(workStartTimeValue);
    
    // Save timer state to localStorage (for backup)
    const jobId = job?._id;
    if (jobId) {
      localStorage.setItem(`isWorking_${jobId}`, 'true');
      localStorage.setItem(`workStartTime_${jobId}`, workStartTimeValue.toString());
    }
    
    // Don't update job status immediately to avoid page reload
    addTimeLog('start_work', 'Počeo rad');
    
    // Update job status to 'In Repair' and save timer state to database
    await updateJobStatus('In Repair', {
      workStartTime: now,
      travelStartTime: null,
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      accumulatedWorkTime: accumulatedWorkTimeRef.current
    });
    
    // Update worker status to 'at_client' so admin can see it
    updateWorkerStatusOnly('at_client', `Radi kod stranke: ${job.clientName || 'Stranka'}`);
  };

  // Parking reminder logic removed - now it's just a one-time modal
  // No need for handleParkingReminder function anymore


  const pauseWork = async () => {
    setIsWorking(false);
    setIsPaused(true);
    // Sačuvaj trenutno vreme rada u ref (oduzmi akumulirano da dobijemo samo trenutno)
    const currentSessionTime = workTime - accumulatedWorkTimeRef.current;
    accumulatedWorkTimeRef.current += currentSessionTime;
    setWorkStartTime(null);
    saveAccumulatedTimes(); // Sačuvaj u localStorage
    
    // Clear work timer from localStorage
    const jobId = job?._id;
    if (jobId) {
      localStorage.removeItem(`isWorking_${jobId}`);
      localStorage.removeItem(`workStartTime_${jobId}`);
    }
    
    // Send WebSocket update immediately to notify admin that work is paused
    // Do this BEFORE saving to database to ensure admin sees the update immediately
    console.log('⏸️ Worker: Pausing work - sending WebSocket update', {
      jobId: job._id,
      workerId: user?.result?._id,
      workerName: user?.result?.name,
      travelStartTime: travelStartTime ? new Date(travelStartTime).toISOString() : (job.travelStartTime ? new Date(job.travelStartTime).toISOString() : null),
      workStartTime: null, // Timer is paused - explicitly set to null
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      accumulatedWorkTime: accumulatedWorkTimeRef.current,
      timestamp: new Date().toISOString()
    });
    
    emitJobStatusUpdate({
      jobId: job._id,
      status: job.status,
      workerId: user?.result?._id,
      workerName: user?.result?.name,
      timestamp: new Date().toISOString(),
      travelStartTime: travelStartTime ? new Date(travelStartTime).toISOString() : (job.travelStartTime ? new Date(job.travelStartTime).toISOString() : null),
      workStartTime: null, // Timer is paused - explicitly set to null
      accumulatedTravelTime: accumulatedTravelTimeRef.current,
      accumulatedWorkTime: accumulatedWorkTimeRef.current
    });
    
    // Save accumulated work time to database
    try {
      const token = user?.token;
      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        {
          accumulatedWorkTime: accumulatedWorkTimeRef.current,
          workStartTime: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving work time to database:', error);
      // Even if database save fails, WebSocket update was already sent
    }
    
    addTimeLog('pause_work', 'Pauzirao rad');
    
    // Update worker status - keep 'at_client' but paused
    updateWorkerStatusOnly('at_client', `Pauzirao rad - Posao: ${job.clientName || 'Stranka'}`);
  };

  const completeJob = async () => {
    setIsTraveling(false);
    setIsWorking(false);
    setIsPaused(false);
    // Sačuvaj trenutno vreme rada u ref (oduzmi akumulirano da dobijemo samo trenutno)
    const currentSessionTime = workTime - accumulatedWorkTimeRef.current;
    accumulatedWorkTimeRef.current += currentSessionTime;
    setTravelStartTime(null);
    setWorkStartTime(null);
    saveAccumulatedTimes(); // Sačuvaj u localStorage
    
    // Clear timer states from localStorage
    const jobId = job?._id;
    if (jobId) {
      localStorage.removeItem(`isTraveling_${jobId}`);
      localStorage.removeItem(`travelStartTime_${jobId}`);
      localStorage.removeItem(`isWorking_${jobId}`);
      localStorage.removeItem(`workStartTime_${jobId}`);
    }
    
    // Save accumulated times to database and clear timer start times
    try {
      const token = user?.token;
      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        {
          accumulatedTravelTime: accumulatedTravelTimeRef.current,
          accumulatedWorkTime: accumulatedWorkTimeRef.current,
          travelStartTime: null,
          workStartTime: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving timer times to database:', error);
    }
    
    // Don't update job status yet - wait for report submission
    addTimeLog('complete', 'Završio posao');
    // Prikaži formu za rezime posla
    setShowReportForm(true);
  };

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Kada radnik otvori formu za rezime, podrazumevano popuni informaciju o garanciji
  // na osnovu samog posla (job.hasWarranty), ali zadrži ručne izmene ako ih je radnik već uneo.
  useEffect(() => {
    if (!showReportForm) return;
    if (!job) return;

    setReportData(prev => ({
      ...prev,
      underWarranty: prev.underWarranty || !!job.hasWarranty
    }));
  }, [showReportForm, job?.hasWarranty]);

  const handleRemoveSparePart = (partId) => {
    setReportData(prev => ({
      ...prev,
      usedSpareParts: prev.usedSpareParts.filter(p => p._id !== partId)
    }));
  };


  // Functions for handling additional materials
  const handleAddAdditionalMaterial = () => {
    setReportData(prev => ({
      ...prev,
      additionalMaterials: [...prev.additionalMaterials, { name: '', price: '', quantity: 1 }]
    }));
  };

  const handleUpdateAdditionalMaterial = (index, field, value) => {
    setReportData(prev => ({
      ...prev,
      additionalMaterials: prev.additionalMaterials.map((material, i) => 
        i === index ? { ...material, [field]: value } : material
      )
    }));
  };

  // Functions for handling selected reversi parts
  const getReversiPartMeta = (selected) => {
    const sparePart =
      selected?.sparePart ||
      (typeof selected?.sparePartId === 'object' ? selected.sparePartId : null);
    const qty = Number(selected?.quantity) || 1;
    const unitSellingPrice =
      selected?.unitSellingPrice != null && selected.unitSellingPrice !== ''
        ? Number(selected.unitSellingPrice)
        : Number(sparePart?.price) || 0;
    const unitCost = Number(sparePart?.purchasePrice) || 0;
    return {
      sparePart,
      name: sparePart?.name || 'Rezervni deo',
      code: sparePart?.code || '',
      qty,
      unitSellingPrice,
      unitCost,
      sellTotal: unitSellingPrice * qty,
      costTotal: unitCost * qty,
    };
  };

  const handleToggleReversiPart = (revers) => {
    const reversId = revers._id || revers.id;
    setSelectedReversiParts(prev => {
      if (prev[reversId]) {
        const newState = { ...prev };
        delete newState[reversId];
        return newState;
      }
      const sparePart =
        revers.sparePart ||
        (typeof revers.sparePartId === 'object' ? revers.sparePartId : null);
      return {
        ...prev,
        [reversId]: {
          ...revers,
          quantity: 1,
          unitSellingPrice: Number(sparePart?.price) || 0,
        }
      };
    });
  };

  const handleUpdateReversiQuantity = (reversId, quantity) => {
    const numQuantity = parseInt(quantity, 10);
    if (isNaN(numQuantity) || numQuantity < 1) return;
    
    const selected = selectedReversiParts[reversId];
    if (!selected) return;
    
    const originalRevers = workerReversi.find(r => (r._id || r.id) === reversId);
    if (!originalRevers) return;
    
    const availableQuantity = originalRevers.quantity;
    if (numQuantity > availableQuantity) return;
    
    setSelectedReversiParts(prev => ({
      ...prev,
      [reversId]: {
        ...prev[reversId],
        quantity: numQuantity
      }
    }));
  };

  const handleUpdateReversiPrice = (reversId, price) => {
    setSelectedReversiParts((prev) => {
      if (!prev[reversId]) return prev;
      return {
        ...prev,
        [reversId]: {
          ...prev[reversId],
          unitSellingPrice: price === '' ? '' : Number(price),
        },
      };
    });
  };

  const handleRemoveAdditionalMaterial = (index) => {
    setReportData(prev => ({
      ...prev,
      additionalMaterials: prev.additionalMaterials.filter((_, i) => i !== index)
    }));
  };

  const submitReport = async () => {
    try {
      setLoading(true);
      const token = user?.token;

      await persistJobSelections();

      // Process selected withdrawn materials: used qty + selling price; unused returns to stock
      const usedWithdrawnEntries = Object.values(selectedWithdrawnMaterials).filter(
        (row) => row && Number(row.quantity) > 0
      );
      for (const row of usedWithdrawnEntries) {
        await markMovementAsUsed(row.movementId, {
          usedQuantity: Number(row.quantity),
          unitSellingPrice:
            row.unitSellingPrice === '' || row.unitSellingPrice == null
              ? undefined
              : Number(row.unitSellingPrice),
          workerId: workerId || undefined,
        });
      }

      const usedSparePartsFromWithdrawn = usedWithdrawnEntries.map((row) => ({
        _id: row.movement?.itemId?._id || row.movement?.itemId || row.movementId,
        name: row.label || 'Materijal',
        code: row.movement?.stockItem?.code || row.movement?.itemId?.itemCode || '',
        price: Number(row.unitSellingPrice) || 0,
        quantity: Number(row.quantity) || 0,
        unitCost: Number(row.unitCost) || 0,
        movementId: row.movementId,
        source: 'withdrawn',
      }));

      // Convert selected reversi parts to usedSpareParts format
      // IMPORTANT: include reversiId so backend can mark those reversi as "installed" (blocks admin from returning).
      const usedSparePartsFromReversi = Object.entries(selectedReversiParts).map(([reversiId, selected]) => {
        const meta = getReversiPartMeta(selected);
        return {
          _id: selected.sparePartId?._id || selected.sparePartId,
          name: meta.name,
          code: meta.code,
          price: meta.unitSellingPrice,
          quantity: meta.qty,
          reversiId
        };
      });

      // Combine with existing usedSpareParts from reportData
      const allUsedSpareParts = [
        ...(reportData.usedSpareParts || []),
        ...usedSparePartsFromReversi,
        ...usedSparePartsFromWithdrawn,
      ];

      // Prihod = samo naplaćena usluga. Materijal ide u rashod po PRODAJNOJ ceni (marža već uračunata).
      const reversiMaterialsExpense = Object.values(selectedReversiParts).reduce(
        (sum, selected) => sum + getReversiPartMeta(selected).sellTotal,
        0
      );

      const withdrawnMaterialsExpense = usedWithdrawnEntries.reduce((sum, row) => {
        return sum + (Number(row.unitSellingPrice) || 0) * (Number(row.quantity) || 0);
      }, 0);
      
      const additionalMaterialsExpense = reportData.additionalMaterials.reduce((sum, material) => {
        if (!material || material.price == null || material.price === '') return sum;
        const price = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
        const qty = material.quantity
          ? typeof material.quantity === 'number'
            ? material.quantity
            : parseFloat(material.quantity) || 1
          : 1;
        return sum + price * qty;
      }, 0);
      
      const amortizationCost = typeof reportData.amortization === 'number' 
        ? reportData.amortization 
        : parseFloat(reportData.amortization) || 0;

      const materialsExpense =
        reversiMaterialsExpense + withdrawnMaterialsExpense + additionalMaterialsExpense;
      const totalCost = materialsExpense + amortizationCost;
      const serviceCharged = parseFloat(reportData.serviceCharged) || 0;
      const profit = serviceCharged - totalCost;
      
      console.log('💰 Calculated costs before sending:', {
        reversiMaterialsExpense,
        withdrawnMaterialsExpense,
        additionalMaterialsExpense,
        materialsExpense,
        amortizationCost,
        totalCost,
        serviceCharged,
        profit,
        selectedReversiPartsCount: Object.keys(selectedReversiParts).length,
        selectedWithdrawnCount: usedWithdrawnEntries.length,
      });
      
      console.log('📊 TROŠKOVI PRI ZAVRŠAVANJU POSLA:', {
        'Prihod (naplaćena usluga)': `${serviceCharged.toLocaleString('sr-RS')} RSD`,
        'Trošak materijala (prodajne cene)': `${materialsExpense.toLocaleString('sr-RS')} RSD`,
        'Amortizacija': `${amortizationCost.toLocaleString('sr-RS')} RSD`,
        'Ukupan rashod': `${totalCost.toLocaleString('sr-RS')} RSD`,
        'Čista zarada': `${profit.toLocaleString('sr-RS')} RSD`
      });

      // Pošalji izveštaj na backend
      // Exclude 'amortization' string field and use only 'amortizationCost' number
      // Also exclude other fields that might interfere
      const { 
        amortization, 
        signatureData, 
        jobNotCompleted,
        nextVisitNotes,
        underWarranty,
        ...reportDataWithoutAmortization 
      } = reportData;
      
      // Format additionalMaterials to ensure price & quantity are numbers
      const formattedAdditionalMaterials = reportData.additionalMaterials.map(material => ({
        name: material.name || '',
        price: typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0,
        quantity: material.quantity ? (typeof material.quantity === 'number' ? material.quantity : parseFloat(material.quantity) || 1) : 1
      }));
      
      // Build report payload explicitly to ensure all cost fields are numbers
      const reportPayload = {
        // Basic report fields
        materialsUsed: reportData.materialsUsed || '',
        workDescription: reportData.workDescription || '',
        serviceCharged: reportData.serviceCharged || '',
        customerSignature: reportData.signatureData || reportData.customerSignature || '',
        customerRating: reportData.customerRating || 10,
        additionalNotes: reportData.additionalNotes || '',
        // Spare parts and materials
        usedSpareParts: allUsedSpareParts,
        additionalMaterials: formattedAdditionalMaterials,
        // Cost fields: materijal po prodajnoj ceni + amortizacija; prihod = samo usluga
        reversiCost: Number(materialsExpense) || 0,
        additionalMaterialsCost: Number(additionalMaterialsExpense) || 0,
        amortizationCost: Number(amortizationCost) || 0,
        totalCost: Number(totalCost) || 0,
        profit: Number(profit) || 0,
        // Time fields
        totalTravelTime: accumulatedTravelTimeRef.current || 0,
        totalWorkTime: accumulatedWorkTimeRef.current || 0,
        // Date fields
        completedAt: new Date().toISOString(),
        submittedAt: new Date().toISOString(),
        fiscalReceiptNumber: reportData.fiscalReceiptNumber || job?.report?.fiscalReceiptNumber || ''
      };
      
      const reportResponse = await axios.post(
        `${API_BASE_URL}/api/jobs/${job._id}/report`,
        reportPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Ažuriraj posao u Redux-u sa odgovorom servera (uključujući report.customerSignature URL)
      if (reportResponse?.data?.job) {
        dispatch(updateJobInArray({ jobId: job._id, updates: reportResponse.data.job }));
      }

      // Prikaži poruku o uspešnom sačuvanju intervencije
      toast.success('Intervencija je uspešno sačuvana.');

      // Ako posao nije završen, kreiraj novi posao u nezakazane
      if (reportData.jobNotCompleted) {
        try {
          const trimmedNextVisitNotes = (reportData.nextVisitNotes || '').trim();
          const originalIssueDescription = (job.issueDescription || '').trim();
          const nextVisitIssueDescription = trimmedNextVisitNotes
            ? `${originalIssueDescription || 'Posao zahteva dodatni rad'}\n\nNapomena za sledeću posetu: ${trimmedNextVisitNotes}`
            : (originalIssueDescription || 'Posao zahteva dodatni rad');

          const newJobData = {
            originJobId: job._id,
            clientName: job.clientName,
            clientPhone: job.clientPhone,
            clientEmail: job.clientEmail || '',
            clientAddress: job.clientAddress,
            deviceTypeId: job.deviceTypeId || job.deviceCategoryId || null,
            deviceType: job.deviceType || job.deviceCategoryName || '-',
            deviceCategoryId: job.deviceCategoryId || job.deviceTypeId || null,
            deviceCategoryName: job.deviceCategoryName || job.deviceType || '',
            deviceBrand: job.deviceBrand || '-',
            deviceModel: job.deviceModel || '-',
            deviceSerialNumber: job.deviceSerialNumber || t('common.notAvailable'),
            issueDescription: nextVisitIssueDescription,
            priority: job.priority || 'Medium',
            status: 'In Pending', // Nekazazani status
            assignedTo: job.assignedTo || 'Unassigned',
            serviceId: job.serviceId || null,
            serviceName: job.serviceName || '',
            servicePrice: job.servicePrice || null,
            serviceDurationMinutes: job.serviceDurationMinutes || null,
            businessType: job.businessType || user?.result?.businessType || 'Home Appliance Technician',
            lastVisitDate: new Date().toISOString(), // Datum današnje posete
            isReturnVisit: true // Flag da je ovo povratna poseta
          };

          await dispatch(createJob({ jobData: newJobData })).unwrap();
          toast.success('Posao je arhiviran i novi posao je kreiran u sekciji nezakazani.');
        } catch (error) {
          console.error('💥 Error creating new job:', error);
          toast.error('Greška pri kreiranju novog posla u nezakazane.');
        }
      }
      
      // Update job status to Finished (not Archived) after report submission
      // Pass customerRating explicitly to ensure it's used
      // Use the rating from reportPayload (which comes from reportData)
      const ratingToSave = reportPayload.customerRating || reportData.customerRating || 10;
      console.log('⭐ Calling promptClientRating with rating:', ratingToSave, 'from reportData:', reportData.customerRating);
      await promptClientRating(ratingToSave);
      await updateJobStatus('Finished');
      
      // Clear localStorage after successful report submission
      const jobId = job?._id;
      if (jobId) {
        localStorage.removeItem(`travelTime_${jobId}`);
        localStorage.removeItem(`workTime_${jobId}`);
        localStorage.removeItem(`isTraveling_${jobId}`);
        localStorage.removeItem(`travelStartTime_${jobId}`);
        localStorage.removeItem(`isWorking_${jobId}`);
        localStorage.removeItem(`workStartTime_${jobId}`);
      }
      
      // Emit WebSocket event for job completion
      emitJobStatusUpdate({
        jobId: job._id,
        status: 'Finished',
        workerId: user?.result?._id,
        workerName: user?.result?.name,
        timestamp: new Date().toISOString(),
        reportSubmitted: true
      });
      
      // Zatvori modal za report form
      setShowReportForm(false);
      setShowCustomerSignatureFullScreen(false);
      
      // Refresh jobs and close modal - call onStatusUpdate first to trigger modal close
      // This will close the job details modal in CalendarWithDragDrop
      if (onStatusUpdate) {
        onStatusUpdate('Finished');
      }
      
      // Also dispatch getJobs to refresh the calendar and update job colors
      const businessType = user?.result?.businessType || 'Home Appliance Technician';
      dispatch(getJobs(businessType));
    } catch (error) {
      console.error('💥 Error submitting report:', error);
      toast.error('Greška pri arhiviranju posla.');
    } finally {
      setLoading(false);
    }
  };

  const addTimeLog = (action, description) => {
    const now = new Date();
    const newLog = {
      action,
      description,
      timestamp: now,
      time: now.toLocaleTimeString()
    };
    setTimeLogs(prev => [...prev, newLog]);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  
  return (
    <div className="bg-gray-800 rounded-lg p-4 mb-4">
      {/* Time Tracker – ne prikazuj za završene poslove (samo Radni nalog i arhiva) */}
      {job?.status !== 'Finished' && (
      <div className="mb-4 p-3 bg-gray-700 rounded-lg">
        {/* Upozorenje ako radnik ima drugi aktivan posao */}
        {hasOtherActiveJob && (
          <div className="mb-2 rounded bg-red-900/40 border border-red-500 px-2 py-1 text-xs text-red-200">
            Već imate aktivan posao{' '}
            <span className="font-semibold">
              {activeJobForWorker?.clientName || activeJobForWorker?._id}
            </span>
            . Pre nego što pokrenete novi posao ili putovanje, završite ili zaustavite postojeći.
          </div>
        )}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-300">Vreme puta:</span>
          <div className="flex items-center gap-2">
            <span className="text-white font-mono">{formatTime(travelTime)}</span>
            <button
              onClick={async () => {
                // Save to database FIRST, then update local state
                try {
                  const token = user?.token;
                  if (token && job?._id) {
                    await axios.patch(
                      `${API_BASE_URL}/api/jobs/${job._id}`,
                      {
                        accumulatedTravelTime: 0,
                        travelStartTime: null
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    // Only update local state after successful save
                    setTravelTime(0);
                    accumulatedTravelTimeRef.current = 0;
                    setTravelStartTime(null);
                    setIsTraveling(false);
                    setIsPaused(false);
                    saveAccumulatedTimes();
                    
                    // Clear from localStorage
                    const jobId = job?._id;
                    if (jobId) {
                      localStorage.removeItem(`isTraveling_${jobId}`);
                      localStorage.removeItem(`travelStartTime_${jobId}`);
                      localStorage.setItem(`travelTime_${jobId}`, '0');
                    }
                    
                    // Send WebSocket update immediately
                    emitJobStatusUpdate({
                      jobId: job._id,
                      status: job.status,
                      workerId: user?.result?._id,
                      workerName: user?.result?.name,
                      timestamp: new Date().toISOString(),
                      travelStartTime: null,
                      workStartTime: workStartTime ? new Date(workStartTime) : job.workStartTime,
                      accumulatedTravelTime: 0,
                      accumulatedWorkTime: accumulatedWorkTimeRef.current
                    });
                    
                    toast.success('Vreme putovanja je resetovano');
                  }
                } catch (error) {
                  console.error('Error resetting travel time:', error);
                  toast.error('Greška pri resetovanju vremena puta');
                }
              }}
              className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors flex items-center gap-1"
              title="Resetuj vreme puta"
            >
              <Square className="h-3 w-3" />
              <span>Reset</span>
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-300">Vreme rada:</span>
          <div className="flex items-center gap-2">
            <span className="text-white font-mono">{formatTime(workTime)}</span>
            <button
              onClick={async () => {
                // Save to database FIRST, then update local state
                try {
                  const token = user?.token;
                  if (token && job?._id) {
                    await axios.patch(
                      `${API_BASE_URL}/api/jobs/${job._id}`,
                      {
                        accumulatedWorkTime: 0,
                        workStartTime: null
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    // Only update local state after successful save
                    setWorkTime(0);
                    accumulatedWorkTimeRef.current = 0;
                    setWorkStartTime(null);
                    setIsWorking(false);
                    setIsPaused(false);
                    saveAccumulatedTimes();
                    
                    // Clear from localStorage
                    const jobId = job?._id;
                    if (jobId) {
                      localStorage.removeItem(`isWorking_${jobId}`);
                      localStorage.removeItem(`workStartTime_${jobId}`);
                      localStorage.setItem(`workTime_${jobId}`, '0');
                    }
                    
                    // Send WebSocket update immediately
                    emitJobStatusUpdate({
                      jobId: job._id,
                      status: job.status,
                      workerId: user?.result?._id,
                      workerName: user?.result?.name,
                      timestamp: new Date().toISOString(),
                      travelStartTime: travelStartTime ? new Date(travelStartTime) : job.travelStartTime,
                      workStartTime: null,
                      accumulatedTravelTime: accumulatedTravelTimeRef.current,
                      accumulatedWorkTime: 0
                    });
                    
                    toast.success('Vreme rada je resetovano');
                  }
                } catch (error) {
                  console.error('Error resetting work time:', error);
                  toast.error('Greška pri resetovanju vremena rada');
                }
              }}
              className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors flex items-center gap-1"
              title="Resetuj vreme rada"
            >
              <Square className="h-3 w-3" />
              <span>Reset</span>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-2">
        {/* SCENARIO 1: Početno stanje - sva tri dugmeta (ne prikazuj za završene poslove) */}
        {!isTraveling && !isWorking && !isPaused && currentStatus !== 'completed' && job?.status !== 'Finished' && (
          <>
            {/* Action buttons - Only show for workers */}
            {isWorker ? (
              <div className="flex gap-2">
                <button
                  onClick={startTravel}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-xs font-medium"
                >
                  <MapPin className="h-4 w-4" />
                  Kreni
                </button>
                <button
                  onClick={startWork}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-xs font-medium"
                >
                  <Wrench className="h-4 w-4" />
                  Start Rad
                </button>
                <button
                  onClick={completeJob}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-xs font-medium"
                >
                  <CheckCircle className="h-4 w-4" />
                  Završi
                </button>
              </div>
            ) : (
              <div className="text-center text-sm text-gray-400 py-2">
                Samo monitoring - radnik kontroliše putovanje i rad
              </div>
            )}
          </>
        )}

        {/* SCENARIO 2: U putovanju - samo zaustavi putovanje (ne prikazuj za završene) */}
        {isTraveling && !isPaused && currentStatus !== 'completed' && job?.status !== 'Finished' && (
          isWorker ? (
            <div className="flex gap-2">
              <button
                onClick={pauseTravel}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <Pause className="h-4 w-4" />
                Zaustavi Putovanje
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-400 py-2">
              Radnik je u putovanju
            </div>
          )
        )}

        {/* SCENARIO 3: Pauziran put - vraća se na početno stanje (ne prikazuj za završene) */}
        {isPaused && !isWorking && currentStatus !== 'completed' && job?.status !== 'Finished' && (
          isWorker ? (
            <div className="flex gap-2">
              <button
                onClick={startTravel}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <MapPin className="h-4 w-4" />
                Kreni
              </button>
              <button
                onClick={startWork}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <Wrench className="h-4 w-4" />
                Start Rad
              </button>
              <button
                onClick={completeJob}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <CheckCircle className="h-4 w-4" />
                Završi
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-400 py-2">
              Putovanje je pauzirano
            </div>
          )
        )}

        {/* SCENARIO 4: U radu - samo zaustavi rad (ne prikazuj za završene) */}
        {isWorking && !isPaused && currentStatus !== 'completed' && job?.status !== 'Finished' && (
          isWorker ? (
            <div className="flex gap-2">
              <button
                onClick={pauseWork}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <Pause className="h-4 w-4" />
                Zaustavi Rad
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-400 py-2">
              Radnik je u radu
            </div>
          )
        )}

        {/* SCENARIO 4.5: Pauziran rad - vraća se na početno stanje (ne prikazuj za završene) */}
        {isPaused && isWorking && currentStatus !== 'completed' && job?.status !== 'Finished' && (
          isWorker ? (
            <div className="flex gap-2">
              <button
                onClick={startTravel}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <MapPin className="h-4 w-4" />
                Kreni
              </button>
              <button
                onClick={startWork}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <Wrench className="h-4 w-4" />
                Start Rad
              </button>
              <button
                onClick={completeJob}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-xs font-medium"
              >
                <CheckCircle className="h-4 w-4" />
                Završi
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-400 py-2">
              Rad je pauziran
            </div>
          )
        )}

        {/* SCENARIO 5: Završen posao (Finished) - prikaži sažetak, opcije štampe i formu za izveštaj */}
        {(currentStatus === 'completed' || currentStatus === 'Finished' || job?.status === 'Finished') && !showReportForm && (
          <div className="text-center">
            <p className="text-green-400 font-medium mb-3">Posao završen!</p>
            <div className="bg-gray-700 rounded-lg p-3 mb-3">
              <div className="text-xs font-medium text-gray-300 mb-1">
                Ukupno vreme puta: <span className="font-mono text-white font-semibold">{formatTime(accumulatedTravelTimeRef.current)}</span>
              </div>
              <div className="text-xs font-medium text-gray-300">
                Ukupno vreme rada: <span className="font-mono text-white font-semibold">{formatTime(accumulatedWorkTimeRef.current)}</span>
              </div>
            </div>
            
            {/* Prikaži izveštaj ako postoji, inače dugme za kreiranje */}
            {job.report ? (
              <div className="bg-gray-700 rounded-lg p-4 mb-3 text-left">
                <h4 className="text-sm font-semibold text-white mb-3">Rezime Posla</h4>
                <div className="space-y-2 text-xs">
                  {/* 1) Ukupno naplaćeno (prvo) + dodatne napomene + datum (u jednoj liniji na desktopu) */}
                  {(job.report.serviceCharged != null && job.report.serviceCharged !== '') ||
                    job.report.additionalNotes ||
                    job.report.submittedAt ? (
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-1">
                      {job.report.serviceCharged != null && job.report.serviceCharged !== '' && (
                        <span>
                          <span className="text-gray-400 font-medium">Ukupno naplaćeno:</span>
                          <span className="text-white font-semibold ml-1">{job.report.serviceCharged} RSD</span>
                        </span>
                      )}
                      {job.report.additionalNotes && (
                        <span>
                          <span className="text-gray-400">Dodatne napomene:</span>
                          <span className="text-white ml-1">{job.report.additionalNotes}</span>
                        </span>
                      )}
                      {job.report.submittedAt && (
                        <span className="text-gray-500">
                          Poslato: {new Date(job.report.submittedAt).toLocaleString('sr-RS')}
                        </span>
                      )}
                    </div>
                  ) : null}

                  {/* 2) Troškovi (zatim) */}
                  {(job.report.usedSpareParts && job.report.usedSpareParts.length > 0) ||
                    (job.report.materialsUsed != null && job.report.materialsUsed !== '') ? (
                    <div className="mt-1">
                      <div className="text-gray-400 font-medium mb-1">Troškovi:</div>
                      <div className="flex flex-col sm:flex-row sm:gap-6">
                        {job.report.usedSpareParts && job.report.usedSpareParts.length > 0 && (
                          <div className="sm:flex-1">
                            <span className="text-gray-400">Rezervni delovi:</span>
                            <div className="ml-2 space-y-1">
                              {job.report.usedSpareParts.map((part, index) => (
                                <div key={index} className="text-white">
                                  {part.name} ({part.code}) × {part.quantity} = {(part.price * part.quantity).toFixed(2)} RSD
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {job.report.materialsUsed != null && job.report.materialsUsed !== '' && (
                          <div className={`sm:flex-1 ${job.report.usedSpareParts && job.report.usedSpareParts.length > 0 ? 'mt-2 sm:mt-0' : ''}`}>
                            <span className="text-gray-400">Dodatni materijali:</span>
                            <span className="text-white ml-2">{job.report.materialsUsed}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* 3) Čista zarada (posle troškova) */}
                  {(() => {
                    const serviceCharged = parseMoneyAmountSR(job.report?.serviceCharged);
                    const totalCost =
                      parseFloat(job.report?.totalCost) ||
                      (parseFloat(job.report?.reversiCost) || 0) +
                        (parseFloat(job.report?.additionalMaterialsCost) || 0) +
                        (parseFloat(job.report?.amortizationCost) || 0);
                    const profit =
                      job.report?.profit != null ? parseFloat(job.report.profit) : serviceCharged - totalCost;

                    // Prikaži samo ako imamo relevantan podatak
                    if (Number.isNaN(profit)) return null;

                    return (
                      <div className="pt-2 border-t border-gray-600 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 font-bold">Čista zarada:</span>
                          <span
                            className={`font-bold text-lg ${
                              profit >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}
                          >
                            {profit.toLocaleString('sr-RS')} RSD
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 3) Opis rada (ako postoji) */}
                  {job.report.workDescription && (
                    <div>
                      <span className="text-gray-400">Opis rada:</span>
                      <span className="text-white ml-2">{job.report.workDescription}</span>
                    </div>
                  )}
                </div>
                
                {renderFinishedJobActions()}
                
                {/* Archive button - only show if job is Archived (after "Završi stranku") */}
                {job?.status === 'Archived' && (
                  <div className="mt-3 text-center text-xs text-gray-400">
                    Posao je arhiviran
                  </div>
                )}
              </div>
            ) : (
              <>
                {isWorker ? (
                  <button
                    onClick={() => setShowReportForm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Popuni Rezime
                  </button>
                ) : (
                  <div className="text-center text-sm text-gray-400 py-2">
                    Posao je završen - čeka se rezime od radnika
                  </div>
                )}
                {renderFinishedJobActions()}
              </>
            )}
          </div>
        )}
      </div>

      {/* Time Logs */}
      {timeLogs.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-medium font-medium text-gray-300 mb-2">Log Aktivnosti</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {timeLogs.map((log, index) => (
              <div key={index} className="flex justify-between items-center text-xs bg-gray-700 p-2 rounded">
                <span className="text-gray-300">{log.description}</span>
                <span className="text-gray-400">{log.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Form Modal */}
      {showReportForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-bold text-white">Rezime o Izvršenom Poslu</h3>
                <p className="text-gray-400 text-xs font-medium mt-1">Popunite detalje o završenom poslu</p>
              </div>
              <button
                onClick={() => {
                  // Reset form data when closing
                  setReportData({
                    materialsUsed: '',
                    workDescription: '',
                    serviceCharged: '',
                    customerSignature: '',
                    customerRating: 10,
                    additionalNotes: '',
                    usedSpareParts: [],
                    additionalMaterials: [],
                    jobNotCompleted: false,
                    nextVisitNotes: '',
                    amortization: '',
                    signatureData: '',
                    underWarranty: false
                  });
                  setShowReportForm(false);
                  setShowCustomerSignatureFullScreen(false);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Job Summary */}
            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-white mb-3">Rezime Posla</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-medium">
                <div>
                  <span className="text-gray-400">Klijent:</span>
                  <span className="text-white ml-2">{formatDisplayValue(job.clientName)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Adresa:</span>
                  <span className="text-white ml-2">{formatDisplayValue(job.clientAddress)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Email:</span>
                  <span className="text-white ml-2">{job.clientEmail || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Kategorija:</span>
                  <span className="text-white ml-2">{job.deviceCategoryName || job.deviceType || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Vreme puta:</span>
                  <span className="text-white ml-2 font-mono">{formatTime(accumulatedTravelTimeRef.current)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Vreme rada:</span>
                  <span className="text-white ml-2 font-mono">{formatTime(accumulatedWorkTimeRef.current)}</span>
                </div>
              </div>
            </div>

            {/* Report Form */}
            <form className="space-y-4">
              {/* Digital Signature - Immediately after Job Summary */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  Digitalni Potpis Stranke
                </label>
                <button
                  type="button"
                  onClick={() => setShowCustomerSignatureFullScreen(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  <Square className="h-4 w-4" />
                  {reportData.signatureData ? 'Ponovo potpiši' : 'Otvori potpis'}
                </button>
              </div>

              {/* Service selection (prvo kategorija, pa tek onda usluga) */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Kategorija usluge
                  </label>
                  <select
                    value={selectedCategoryId || ''}
                    onChange={(e) => {
                      const newCategoryId = e.target.value || '';
                      setSelectedCategoryId(newCategoryId);

                      const cat = deviceCategories.find((c) =>
                        normalizeId(c.id || c._id) === newCategoryId
                      );
                      if (cat?.name) {
                        setSelectedCategoryName(cat.name);
                      }

                      // Kada se promeni kategorija, resetuj izabranu uslugu
                      setSelectedServiceId('');
                      setSelectedServiceName('');
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Izaberi kategoriju --</option>
                    {deviceCategories.map((category) => (
                      <option
                        key={category.id || category._id}
                        value={normalizeId(category.id || category._id)}
                      >
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCategoryId && (
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-2">
                      Izabrana usluga
                    </label>
                    <select
                      value={selectedServiceId}
                      onChange={handleWarrantyServiceSelect}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Izaberi uslugu --</option>
                      {warrantyServiceOptions.map((service) => (
                        <option
                          key={service.id || service._id}
                          value={normalizeId(service.id || service._id)}
                        >
                          {service.name}
                          {service.price != null &&
                            ` - ${Number(service.price).toLocaleString('sr-RS')} RSD`}
                          {!hasMeasuredDuration &&
                            service.durationMinutes != null &&
                            ` (${service.durationMinutes} min)`}
                        </option>
                      ))}
                    </select>
                    {workerSelectedService && (
                      <p className="mt-1 text-xs text-gray-400">
                        Izabrana usluga: {workerSelectedService.name}
                        {workerSelectedService.price != null &&
                          ` • ${Number(workerSelectedService.price).toLocaleString('sr-RS')} RSD`}
                        {!hasMeasuredDuration &&
                          workerSelectedService.durationMinutes != null &&
                          ` • ${workerSelectedService.durationMinutes} min`}
                        {reportData.underWarranty || job?.hasWarranty
                          ? ' • Garancija – podrazumevano naplata 0 RSD (možeš ručno promeniti).'
                          : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Service Charged and Warranty in same row */}
              <div className="flex items-start gap-4">
                {/* Left: Service Charged */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Naplaćena Usluga (RSD)
                  </label>
                  <input
                    type="number"
                    name="serviceCharged"
                    value={reportData.serviceCharged}
                    onChange={handleReportChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                {/* Right: Warranty Checkbox */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Garancija
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={reportData.underWarranty}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setReportData(prev => ({
                          ...prev,
                          underWarranty: checked,
                          // Ako se sada uključuje garancija, podrazumevano naplata klijentu 0
                          // (radnik i dalje može ručno da promeni posle toga).
                          ...(checked ? { serviceCharged: '0' } : {})
                        }));
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-xs font-medium text-gray-300">
                      Aparat je bio u garanciji
                    </span>
                  </label>
                </div>
              </div>

              {/* Materijal – stack dugmadi (mobilno-friendly) */}
              <div className="space-y-3">
                <p className="text-xs font-medium text-gray-300">Materijal i troškovi</p>

                {/* 1. Revers */}
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-1">
                    Delovi iz reversa
                  </label>
                  {showReversiBox ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <select
                          value={selectedReversiCategoryId || ''}
                          onChange={(e) => setSelectedReversiCategoryId(e.target.value || '')}
                          className="flex-1 px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">-- Kategorija uređaja (sve) --</option>
                          {deviceCategories.map((category) => (
                            <option
                              key={category.id || category._id}
                              value={normalizeId(category.id || category._id)}
                            >
                              {category.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowReversiBox(false)}
                          className="px-2 py-1 text-gray-300 text-xs bg-gray-800 border border-gray-600 rounded hover:bg-gray-700"
                          title="Sakrij listu delova iz reversa"
                        >
                          ×
                        </button>
                      </div>
                      {reversiLoading ? (
                        <div className="text-center py-4 text-gray-400 text-xs bg-gray-700 rounded-lg">Učitavanje...</div>
                      ) : workerReversi.length > 0 ? (
                        <div className="bg-gray-700 rounded-lg p-3 space-y-2">
                          <input
                            type="text"
                            placeholder="Pretraži rezervne delove..."
                            value={reversiSearchTerm}
                            onChange={(e) => setReversiSearchTerm(e.target.value)}
                            className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <div className="max-h-48 overflow-y-auto space-y-2">
                            {filteredReversiPartsList.length === 0 && selectedReversiCategoryId ? (
                              <div className="py-4 text-center text-gray-400 text-xs bg-gray-800 rounded-lg px-3">
                                Nema rezervnih delova na reversu za izabranu kategoriju.
                                {reversiSearchTerm && ' Pokušaj drugačiju pretragu.'}
                              </div>
                            ) : (
                              filteredReversiPartsList.map(({ revers, part }) => {
                                const reversId = revers._id || revers.id;
                                const partId = (part._id || part.id) || reversId;
                                const partName = part.name || 'Nepoznat deo';
                                const partCode = part.code || '';
                                const partPrice = part.price || 0;
                                const availableQuantity = revers.quantity || 0;
                                const isInstalled = !!revers.installed;
                                const isSelected = !!selectedReversiParts[reversId];
                                const selectedRow = selectedReversiParts[reversId];
                                const selectedQuantity = selectedRow?.quantity || 1;
                                const selectedSellPrice =
                                  selectedRow?.unitSellingPrice != null && selectedRow.unitSellingPrice !== ''
                                    ? Number(selectedRow.unitSellingPrice)
                                    : partPrice;

                                return (
                                  <div key={reversId} className="bg-gray-600 rounded p-2">
                                    <div className="flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        disabled={isInstalled}
                                        onChange={() =>
                                          handleToggleReversiPart({
                                            _id: reversId,
                                            sparePartId: partId,
                                            sparePart: part,
                                            quantity: availableQuantity
                                          })
                                        }
                                        className="mt-1 w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="text-white text-xs font-medium truncate">
                                            {partName} {partCode && `(${partCode})`}
                                          </span>
                                          <span className="text-gray-400 text-xs whitespace-nowrap">
                                            {isInstalled ? 'Ugrađen' : `Na stanju: ${availableQuantity}`}
                                          </span>
                                        </div>
                                        {isSelected && !isInstalled ? (
                                          <div className="mt-2 grid grid-cols-2 gap-2">
                                            <div>
                                              <label className="block text-[10px] text-gray-400 mb-0.5">Kom</label>
                                              <input
                                                type="number"
                                                min="1"
                                                max={availableQuantity}
                                                value={selectedQuantity}
                                                onChange={(e) =>
                                                  handleUpdateReversiQuantity(
                                                    reversId,
                                                    e.target.value
                                                  )
                                                }
                                                className="w-full px-2 py-0.5 bg-gray-700 border border-gray-500 rounded text-white text-xs text-center focus:ring-1 focus:ring-blue-500"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-[10px] text-gray-400 mb-0.5">Cena / kom</label>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={selectedRow?.unitSellingPrice ?? partPrice}
                                                onChange={(e) =>
                                                  handleUpdateReversiPrice(reversId, e.target.value)
                                                }
                                                className="w-full px-2 py-0.5 bg-gray-700 border border-gray-500 rounded text-white text-xs text-center focus:ring-1 focus:ring-blue-500"
                                              />
                                            </div>
                                            <p className="col-span-2 text-[10px] text-gray-300">
                                              = {(selectedSellPrice * selectedQuantity).toLocaleString('sr-RS')} RSD
                                            </p>
                                          </div>
                                        ) : (
                                          <div className="mt-1 text-xs text-gray-300">
                                            {partPrice.toLocaleString('sr-RS')} RSD
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-700 rounded-lg p-3 text-center text-gray-400 text-xs">
                          Nema materijala iz reversa
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowReversiBox(true)}
                      className="w-full px-2 py-2 bg-gray-800 border border-gray-600 rounded text-xs text-gray-200 hover:bg-gray-700"
                    >
                      {Object.keys(selectedReversiParts).length > 0
                        ? `Prikaži listu delova iz reversa (${Object.keys(selectedReversiParts).length} izabrano)`
                        : 'Prikaži listu delova iz reversa'}
                    </button>
                  )}
                </div>

                {/* 2. Povučena roba – ispod reversa */}
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-1">
                    Povučena roba za posao
                  </label>
                  <JobWithdrawnMaterialsPicker
                    jobId={job?._id}
                    selected={selectedWithdrawnMaterials}
                    onChange={setSelectedWithdrawnMaterials}
                    open={showWithdrawnBox}
                    onOpenChange={setShowWithdrawnBox}
                  />
                </div>

                {/* 3. Dodatni materijal */}
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-1">
                    Dodatni delovi / materijal
                  </label>
                  {!showAdditionalMaterialsBox ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowAdditionalMaterialsBox(true);
                        if (reportData.additionalMaterials.length === 0) {
                          handleAddAdditionalMaterial();
                        }
                      }}
                      className="w-full px-2 py-2 bg-gray-800 border border-gray-600 rounded text-xs text-gray-200 hover:bg-gray-700"
                    >
                      {reportData.additionalMaterials.length > 0
                        ? `Prikaži dodatni materijal (${reportData.additionalMaterials.length})`
                        : '+ Dodaj materijal'}
                    </button>
                  ) : (
                    <>
                      <div className="flex justify-end mb-2">
                        <button
                          type="button"
                          onClick={() => setShowAdditionalMaterialsBox(false)}
                          className="px-2 py-1 text-gray-300 text-xs bg-gray-800 border border-gray-600 rounded hover:bg-gray-700"
                          title="Sakrij dodatni materijal"
                        >
                          ×
                        </button>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-3 space-y-2">
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {reportData.additionalMaterials.map((material, index) => {
                            const qty = material.quantity ?? 1;
                            const unitPrice = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
                            const subtotal = unitPrice * qty;
                            return (
                              <div key={index} className="bg-gray-600 rounded p-2 text-xs text-gray-200">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="naziv (npr. lepak)"
                                    value={material.name}
                                    onChange={(e) => handleUpdateAdditionalMaterial(index, 'name', e.target.value)}
                                    className="flex-1 min-w-0 px-2 py-1 bg-gray-700 border border-gray-500 rounded text-white text-xs placeholder-gray-400 focus:ring-1 focus:ring-blue-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAdditionalMaterial(index)}
                                    className="shrink-0 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                  >
                                    ×
                                  </button>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      placeholder="cena"
                                      value={material.price}
                                      onChange={(e) => handleUpdateAdditionalMaterial(index, 'price', e.target.value)}
                                      className="w-16 px-1 py-0.5 bg-gray-700 border border-gray-500 rounded text-white text-xs text-center placeholder-gray-400 focus:ring-1 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-300">RSD</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-400">Kom:</span>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={qty}
                                      onChange={(e) => handleUpdateAdditionalMaterial(index, 'quantity', e.target.value)}
                                      className="w-10 px-1 py-0.5 bg-gray-700 border border-gray-500 rounded text-white text-xs text-center placeholder-gray-400 focus:ring-1 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-400 whitespace-nowrap">
                                      = {subtotal.toLocaleString('sr-RS')} RSD
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={handleAddAdditionalMaterial}
                          className="w-full flex items-center justify-center gap-1 px-2 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                        >
                          <span>+</span>
                          <span>Dodaj još materijal</span>
                        </button>
                        {reportData.additionalMaterials.length > 0 && (
                          <div className="pt-2 border-t border-gray-600">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-300">Ukupno dodatno:</span>
                              <span className="text-xs font-bold text-white">
                                {reportData.additionalMaterials.reduce((sum, material) => {
                                  const qty = material.quantity ? parseFloat(material.quantity) || 1 : 1;
                                  const price = parseFloat(material.price) || 0;
                                  return sum + price * qty;
                                }, 0).toLocaleString('sr-RS')} RSD
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

                {/* Evidencija – lista svega što je dodato (revers + povučena roba + dodatni materijal) */}
                {(Object.keys(selectedReversiParts).length > 0 ||
                  Object.keys(selectedWithdrawnMaterials).length > 0 ||
                  reportData.additionalMaterials.length > 0) && (
                  <div className="mt-4 p-3 bg-gray-800 border border-gray-600 rounded-lg">
                    <div className="text-xs font-semibold text-gray-200 mb-2 border-b border-gray-600 pb-2">
                      Evidencija – uključeno u izveštaj
                    </div>
                    <ul className="space-y-1.5 text-xs">
                      {Object.entries(selectedWithdrawnMaterials).map(([movementId, row]) => {
                        const qty = Number(row.quantity) || 0;
                        const sell = Number(row.unitSellingPrice) || 0;
                        const subtotal = sell * qty;
                        const unused = Math.max(0, (Number(row.maxQuantity) || 0) - qty);
                        return (
                          <li key={movementId} className="flex items-center justify-between gap-2 py-1.5 px-2 bg-gray-700 rounded">
                            <div className="flex-1 min-w-0 text-gray-200 flex flex-col">
                              <span className="truncate">
                                <span className="text-amber-300 font-medium">Magacin:</span>{' '}
                                {row.label || 'Materijal'}
                              </span>
                              <span className="text-gray-400">
                                Utrošeno: {qty} · {sell.toLocaleString('sr-RS')} RSD/kom →{' '}
                                {subtotal.toLocaleString('sr-RS')} RSD
                                {unused > 0 ? ` · vraća se ${unused}` : ''}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedWithdrawnMaterials((prev) => {
                                  const next = { ...prev };
                                  delete next[movementId];
                                  return next;
                                })
                              }
                              className="shrink-0 p-1 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded"
                              title="Ukloni iz izveštaja"
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                      {Object.entries(selectedReversiParts).map(([reversId, selected]) => {
                        const meta = getReversiPartMeta(selected);
                        const code = meta.code ? ` (${meta.code})` : '';
                        return (
                          <li key={reversId} className="flex items-center justify-between gap-2 py-1.5 px-2 bg-gray-700 rounded">
                            <div className="flex-1 min-w-0 text-gray-200 flex flex-col">
                              <span className="truncate">
                                <span className="text-blue-300 font-medium">Revers:</span>{' '}
                                {meta.name}{code}
                              </span>
                              <span className="text-gray-400">
                                Kom: {meta.qty} · {meta.unitSellingPrice.toLocaleString('sr-RS')} RSD →{' '}
                                {meta.sellTotal.toLocaleString('sr-RS')} RSD
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleReversiPart(selected)}
                              className="shrink-0 p-1 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded"
                              title="Ukloni iz izveštaja"
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                      {reportData.additionalMaterials.map((material, index) => {
                        const qty = material.quantity ? parseFloat(material.quantity) || 1 : 1;
                        const unitPrice = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
                        const subtotal = unitPrice * qty;
                        const name = material.name?.trim() || 'Materijal';
                        return (
                          <li key={`add-${index}`} className="flex items-center justify-between gap-2 py-1.5 px-2 bg-gray-700 rounded">
                            <div className="flex-1 min-w-0 text-gray-200 flex flex-col">
                              <span className="truncate">
                                <span className="text-amber-300 font-medium">Dodatno:</span> {name}
                              </span>
                              <span className="text-gray-400">
                                Kom: {qty} · {unitPrice.toLocaleString('sr-RS')} RSD → {subtotal.toLocaleString('sr-RS')} RSD
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="mt-2 pt-2 border-t border-gray-600 flex justify-between text-xs font-semibold text-gray-200">
                      <span>Ukupno materijal (trošak):</span>
                      <span>
                        {(
                          Object.values(selectedWithdrawnMaterials).reduce((sum, row) => {
                            return sum + (Number(row.unitSellingPrice) || 0) * (Number(row.quantity) || 0);
                          }, 0) +
                          Object.values(selectedReversiParts).reduce(
                            (sum, selected) => sum + getReversiPartMeta(selected).sellTotal,
                            0
                          ) +
                          reportData.additionalMaterials.reduce((sum, m) => {
                            const qty = m.quantity ? parseFloat(m.quantity) || 1 : 1;
                            const unitPrice = typeof m.price === 'number' ? m.price : parseFloat(m.price) || 0;
                            return sum + unitPrice * qty;
                          }, 0)
                        ).toLocaleString('sr-RS')} RSD
                      </span>
                    </div>
                  </div>
                )}

              {/* Amortization */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  Amortizacija (trošak vozila)
                </label>
                <input
                  type="number"
                  name="amortization"
                  value={reportData.amortization}
                  onChange={handleReportChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Profit Calculation */}
              {(() => {
                const materialsExpense =
                  Object.values(selectedReversiParts).reduce(
                    (sum, selected) => sum + getReversiPartMeta(selected).sellTotal,
                    0
                  ) +
                  Object.values(selectedWithdrawnMaterials).reduce(
                    (sum, row) => sum + (Number(row.unitSellingPrice) || 0) * (Number(row.quantity) || 0),
                    0
                  ) +
                  reportData.additionalMaterials.reduce((sum, material) => {
                    const qty = material.quantity ? parseFloat(material.quantity) || 1 : 1;
                    const unitPrice = parseFloat(material.price) || 0;
                    return sum + unitPrice * qty;
                  }, 0);
                const amortizationCost = parseFloat(reportData.amortization) || 0;
                const serviceCharged = parseFloat(reportData.serviceCharged) || 0;
                const totalExpense = materialsExpense + amortizationCost;
                const profit = serviceCharged - totalExpense;

                return (
                  <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="space-y-3 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Prihod</div>
                        <div className="flex items-center justify-between font-semibold">
                          <span className="text-gray-300">Naplaćeno od klijenta</span>
                          <span className="text-white">{serviceCharged.toLocaleString('sr-RS')} RSD</span>
                        </div>
                      </div>

                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Rashod</div>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Materijal (prodajne cene)</span>
                            <span className="text-white">{materialsExpense.toLocaleString('sr-RS')} RSD</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Amortizacija</span>
                            <span className="text-white">{amortizationCost.toLocaleString('sr-RS')} RSD</span>
                          </div>
                          <div className="flex items-center justify-between pt-1 border-t border-gray-600 font-semibold">
                            <span className="text-gray-300">Ukupno rashod</span>
                            <span className="text-white">{totalExpense.toLocaleString('sr-RS')} RSD</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-500">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-200 font-bold">Čista zarada</span>
                          <span className={`font-bold text-lg ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {profit.toLocaleString('sr-RS')} RSD
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1">
                          {serviceCharged.toLocaleString('sr-RS')} − {totalExpense.toLocaleString('sr-RS')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  Napomena
                </label>
                <textarea
                  name="additionalNotes"
                  value={reportData.additionalNotes}
                  onChange={handleReportChange}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Dodatne napomene..."
                />
              </div>

              {/* Rating and Job Not Completed in same row */}
              <div className="flex items-start gap-4">
                {/* Left: Rating */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Ocena
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const current = reportData.customerRating || 10;
                        const newValue = Math.max(1, current - 1);
                        setReportData(prev => ({ ...prev, customerRating: newValue }));
                      }}
                      className="px-2 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-l-lg border border-gray-600 border-r-0 focus:ring-2 focus:ring-blue-500"
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="2"
                      value={reportData.customerRating === '' || reportData.customerRating === null || reportData.customerRating === undefined ? '' : reportData.customerRating}
                      onChange={(e) => {
                        let inputValue = e.target.value;
                        
                        // Remove any non-numeric characters
                        inputValue = inputValue.replace(/[^0-9]/g, '');
                        
                        // Allow empty input during typing (for deletion)
                        if (inputValue === '') {
                          setReportData(prev => ({ ...prev, customerRating: '' }));
                          return;
                        }
                        
                        // Parse the value
                        const numValue = parseInt(inputValue, 10);
                        
                        // Update if it's a valid number
                        if (!isNaN(numValue)) {
                          // Clamp value between 1 and 10
                          const clampedValue = Math.max(1, Math.min(10, numValue));
                          setReportData(prev => ({ ...prev, customerRating: clampedValue }));
                        }
                      }}
                      onBlur={(e) => {
                        // If empty on blur, set to default 10
                        const inputValue = e.target.value.trim();
                        if (inputValue === '' || inputValue === null || inputValue === undefined) {
                          setReportData(prev => ({ ...prev, customerRating: 10 }));
                          return;
                        }
                        
                        const value = parseInt(inputValue, 10);
                        if (isNaN(value) || value < 1 || value > 10) {
                          setReportData(prev => ({ ...prev, customerRating: 10 }));
                        } else {
                          setReportData(prev => ({ ...prev, customerRating: value }));
                        }
                      }}
                      className="w-20 px-3 py-2 bg-gray-700 border-y border-gray-600 text-white text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = reportData.customerRating || 10;
                        const newValue = Math.min(10, current + 1);
                        setReportData(prev => ({ ...prev, customerRating: newValue }));
                      }}
                      className="px-2 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-r-lg border border-gray-600 border-l-0 focus:ring-2 focus:ring-blue-500"
                    >
                      +
                    </button>
                    <span className="text-sm text-gray-400">/10</span>
                  </div>
                </div>

                {/* Right: Job Not Completed Checkbox */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Status posla
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportData.jobNotCompleted}
                      onChange={(e) => setReportData(prev => ({ ...prev, jobNotCompleted: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-xs font-medium text-gray-300">
                      Posao nije završen
                    </span>
                  </label>
                </div>
              </div>

              {/* Next Visit Notes - Show only if jobNotCompleted is checked */}
              {reportData.jobNotCompleted && (
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Šta treba poneti sledeći put
                  </label>
                  <textarea
                    name="nextVisitNotes"
                    value={reportData.nextVisitNotes}
                    onChange={handleReportChange}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Napomena o tome šta treba poneti ili uraditi sledeći put (npr. rezervni deo, alat, dodatni materijal...)"
                  />
                </div>
              )}

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowReportForm(false);
                    setShowCustomerSignatureFullScreen(false);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Otkaži
                </button>
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Čuva se...' : 'Završi stranku'}
                </button>
              </div>
            </form>

            {/* Full-screen signature overlay */}
            {showCustomerSignatureFullScreen && (
              <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 rounded-lg w-full h-full overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b border-gray-700">
                    <h3 className="text-sm font-semibold text-white">Digitalni potpis stranke</h3>
                    <button
                      type="button"
                      onClick={() => setShowCustomerSignatureFullScreen(false)}
                      className="text-gray-300 hover:text-white"
                      aria-label="Zatvori potpis"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-auto p-4">
                    <SignaturePad
                      signatureData={reportData.signatureData}
                      onSignatureChange={(data) => setReportData((prev) => ({ ...prev, signatureData: data }))}
                    />
                  </div>

                  <div className="p-4 border-t border-gray-700 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCustomerSignatureFullScreen(false)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      Sačuvaj i zatvori
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Print Preview Modal for Radni Nalog */}
      {showPrintModal && (
        <WorkOrderPrintModal
          job={job}
          onClose={() => setShowPrintModal(false)}
          travelTimeSeconds={accumulatedTravelTimeRef.current}
          workTimeSeconds={accumulatedWorkTimeRef.current}
        />
      )}

      {/* AI Diagnosis Modal */}
      <AIDiagnosisModal
        isOpen={showAIDiagnosisModal}
        onClose={() => {
          console.log('🤖 [AI DIAGNOSIS] Modal onClose called');
          setShowAIDiagnosisModal(false);
        }}
        diagnosisData={aiDiagnosisData}
        loading={aiDiagnosisLoading}
        deviceInfo={job ? `${job.deviceType || ''} ${job.deviceBrand || ''} ${job.deviceModel || ''}`.trim() : ''}
        issueDescription={job?.issueDescription || ''}
        jobId={job?._id}
        userToken={user?.token}
        onRatingSubmit={async (ratingData) => {
          try {
            // Log rating for analytics (can be sent to backend later)
            console.log('⭐ Rating submitted:', ratingData);
            // For now, just show success - can add API call later
            toast.success('Hvala na oceni!');
          } catch (error) {
            console.error('Error submitting rating:', error);
          }
        }}
      />

      {/* Parking Reminder Modal - Simple one-time reminder */}
      {showParkingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Parking</h3>
                <p className="text-gray-400 text-sm">Da li ste uplatili parking?</p>
              </div>
              <button
                onClick={() => {
                  setShowParkingModal(false);
                  proceedWithStartWork();
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowParkingModal(false);
                  proceedWithStartWork();
                }}
                className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              >
                Da, uplatio sam
              </button>
              <button
                onClick={() => {
                  setShowParkingModal(false);
                  proceedWithStartWork();
                }}
                className="w-full px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
              >
                Preskoči
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize with awareness of timer-related fields so real-time updates (pause/stop/reset)
// odmah stižu i do admin i do radničkog UI-a
export default React.memo(JobStatusControls, (prevProps, nextProps) => {
  const prevJob = prevProps.job || {};
  const nextJob = nextProps.job || {};

  return (
    // Isti posao
    prevJob?._id === nextJob?._id &&
    // Status nije promenjen
    prevJob?.status === nextJob?.status &&
    // Timer polja ista – ako se razlikuju, moramo da rerenderujemo
    prevJob?.travelStartTime === nextJob?.travelStartTime &&
    prevJob?.workStartTime === nextJob?.workStartTime &&
    (prevJob?.accumulatedTravelTime ?? 0) === (nextJob?.accumulatedTravelTime ?? 0) &&
    (prevJob?.accumulatedWorkTime ?? 0) === (nextJob?.accumulatedWorkTime ?? 0) &&
    // Isti korisnik / token
    prevProps.user?.token === nextProps.user?.token
  );
});
